import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { StepperOrientation, StepperSelectionEvent } from '@angular/cdk/stepper';
import { ChangeDetectorRef, Component, ElementRef, OnInit, OnDestroy, TemplateRef, ViewChild, ViewChildren, QueryList, AfterViewInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { Observable, Subscription, interval, map, switchMap, takeUntil } from 'rxjs';
import { SaIdValidator } from 'src/app/shared/validators/sa-id.validator';
import { EmergencyContactValidator } from 'src/app/shared/validators/emergency-contact.validator';
import { FileObject } from './../../../core/interfaces/file-object';
import { BaseComponent } from './../../../core/components/base/base.component';
import { COUNTRIES, TITLES } from './../../../core/data/app.data';
import { FeedbackService } from './../../../core/services/feedback.service';
import {
    InviteDocumentPreviousFile,
    InviteDocumentRequestPayload,
    InviteDocumentUploadResult,
    PatientInviteService,
} from './../../../core/services/patient-invite.service';
import {
    ACCEPTED_RETURN_TYPES,
    DocumentFillMode,
    EMBEDDED_FILL_MODE,
    isAcceptedReturnFile,
    readFillMode,
    readPublicDocumentMessage,
    readPublicDocumentState,
    readUploadRefusal,
} from './../../../core/services/patient-document-copy';
import {
    DocumentFillDialogComponent,
    DocumentFillDialogData,
    DocumentFillDialogResult,
    DocumentFillUnavailableReason,
} from './document-fill-dialog/document-fill-dialog.component';
import { FileDownloadService } from './../../../core/services/file-download.service';
import { ScrollPositionService } from './../../../core/services/scroll-position.service';
import { SignaturePadComponent } from './../../../shared/components/signature-pad/signature-pad.component';
import { MatSelectChange } from '@angular/material/select';
import { FormUtils } from 'src/app/core/utils/form.utils';
import { MedicalAidPick, medicalAidFields } from 'src/app/core/scheme-reference/scheme-pick';
import type { SchemeEntry } from 'src/app/core/scheme-reference/scheme-reference-loader';
import { MedicalAidPickerInitial } from 'src/app/shared/components/medical-aid-picker/medical-aid-picker.component';
import { DynamicFormRendererComponent } from './../../../shared/components/dynamic-form-renderer/dynamic-form-renderer.component';
import { FormAnswers, FormSchema, FormSection } from './../../../shared/models/custom-form.model';

/**
 * One practice form riding inside this invite — `tasks/patient-forms.md` §6, §9 phase 3.
 *
 * Exactly what `loadInviteById` returns per pending `PatientFormRequest`, plus the two pieces of
 * paging state the wizard keeps for it. `schema` is the PINNED version's, so a form republished
 * while the patient had this page open still renders against the version they were asked.
 */
interface InviteFormRequest {
    /** The `PatientFormRequest` id. Sent back on submit; it is the only id the wizard holds. */
    id: string;
    name: string;
    description: string | null;
    requiresSignature: boolean;
    schema: FormSchema;
    /** The live answers, owned by the renderer. This component only holds and posts them. */
    answers: FormAnswers;
    /**
     * The VISIBLE sections, learned from the renderer's `sectionsChange` (§14.2) rather than read
     * off the schema — a section whose questions are all hidden by `showIf` is not a wizard step.
     */
    sections: FormSection[];
    /** Which section this form's renderer is currently paging to. An id, never an index (§14.2). */
    activeSectionId: string | null;
}

/**
 * One practice DOCUMENT riding inside this invite — `tasks/practice-documents.md` §19.
 *
 * Download the blank → complete it away from the screen → send the pages back. This is the whole
 * of the tier-C loop and THE ONLY PLACE IT LIVES: the standalone `/patient-document/:token` page
 * that used to run it is retired (§19), so a patient gets ONE link and the practice gets one
 * approved WhatsApp template (§19.1).
 *
 * ── THE ASYMMETRY WITH FORMS, WHICH IS DELIBERATE ─────────────────────────────────────────────
 *
 * A form's answers ride the single invite `PUT` at the end. A document does NOT: it is returned
 * OUT OF BAND, immediately, on its own step. `submit()` builds one `FormData` carrying `data` and
 * `signature`, and adding tens of megabytes of photographs to it would make the last button in the
 * wizard the one most likely to time out — and would lose every page if it did.
 *
 * Everything below the first block is per-document UI state. It is held HERE rather than in a
 * child component for the same reason the renderers are kept in the DOM behind `[hidden]`: a
 * patient who steps back to check something must not return to an emptied file list, and an upload
 * in flight must survive them navigating away and back.
 */
interface InviteDocumentRequest {
    /** The `PatientDocumentRequest` id — the only handle that is ever posted back. */
    id: string;
    name: string;
    description: string | null;
    pageCount: number | null;
    /** What the patient's download is called. */
    fileName: string | null;
    /** The HARD BLOCK. `true` and nothing returned ⇒ `isStepValid` is false for this step. */
    requiredInInvite: boolean;

    /* ── THE TIER — `tasks/practice-documents.md` §1.1, §5 ──────────────────────────────────── */

    /**
     * `'embedded'` (tier A) or `'download'` (tier C), as the SERVER resolved it.
     *
     * Never derived here and never guessed: `readFillMode` reads `'embedded'` and nothing else as
     * tier A, so a payload from a server that predates Phase 5, a null, or a tier this client has
     * never heard of all land on the download path — the one that works for every document
     * (§3.2 decision 4).
     */
    fillMode: DocumentFillMode;

    /**
     * Set when a document that CLAIMED `embedded` turned out not to be fillable here.
     *
     * The viewer is then withdrawn for the rest of this step and the download path stands alone —
     * §5's "must not trap the patient in a viewer they cannot complete". It is deliberately not a
     * bare boolean: the three reasons need three different sentences, and a patient told "that
     * did not work" about a document with no fillable boxes will keep trying.
     */
    embeddedUnavailableReason: DocumentFillUnavailableReason | null;

    /* ── The server's own caps, published so this client cannot drift LOOSER than them ──────── */
    maxFiles: number | null;
    maxTotalBytes: number | null;
    /** The server's sentence about what it takes. Printed, never paraphrased. */
    accepts: string | null;

    /* ── The blank, and its short-lived URL ─────────────────────────────────────────────────── */
    blankUrl: string | null;
    /** How long `blankUrl` is good for, on the SERVER's clock. Drives the re-mint cadence. */
    blankExpiresInMinutes: number | null;
    /** When `blankUrl` was minted. 0 means "never asked". */
    blankMintedAt: number;
    blankLoading: boolean;
    blankError: string | null;

    /* ── SENT BACK — `tasks/document-rejection-resend.md` ─────────────────────────── */

    /**
     * This document was returned once and the practice sent it back. The step says so INSTEAD of
     * "there are two steps: download the document, then send the completed pages back" — which,
     * to somebody who has already done exactly that, reads as though the practice lost it.
     */
    resubmission: boolean;
    /** The reviewer's own words about what needs to change. The reason this email arrived. */
    previousNote: string | null;
    /** How many pages they sent last time. 0 means there is nothing to offer them. */
    previousFileCount: number;

    /* The previous attempt, fetched on demand — the links expire in minutes, and most patients
       will not ask, so nothing is minted until they do. */
    previousFiles: InviteDocumentPreviousFile[];
    previousLoading: boolean;
    previousError: string | null;
    previousOpen: boolean;

    /* ── The patient's selection. NEVER replaced by a response. ─────────────────────────────── */
    files: File[];
    /**
     * The DROP ZONE's own array, not ours. `<app-file-upload>` pushes a `FileObject` into the
     * array it is given and emits the whole array, so it has to own one — but the authoritative
     * list is `files` above. This is drained to empty every time we consume an emission, so the
     * component never re-offers a page the patient has already added.
     */
    pickerObjects: FileObject[];
    /** Files the picker handed over that we will not send, named so the patient can see why. */
    rejectedFileNames: string[];
    /** A cap the selection breaks, in the server's own numbers. Blocks the send until fixed. */
    limitError: string | null;

    /* ── Upload ─────────────────────────────────────────────────────────────────────────────── */
    isUploading: boolean;
    /** 0–100, or `null` before the browser knows the total. */
    uploadPercent: number | null;
    uploadError: string | null;

    /** Returned. Set by a successful upload AND by `alreadyReturned`, which means the same thing. */
    returned: boolean;
    returnedMessage: string;
}

@Component({
    selector: 'app-patient-invite-wizard',
    templateUrl: './patient-invite-wizard.component.html',
    styleUrls: ['./patient-invite-wizard.component.scss']
})
export class PatientInviteWizardComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    todayDate: moment.Moment = moment();
    @ViewChild('scrollableParent', { static: false }) scrollableParent!: ElementRef;
    @ViewChild(SignaturePadComponent) signaturePadComponent!: SignaturePadComponent;

    isLinear = true; // Set to true for wizard-style progression
    readonly incompleteStepMessage = 'Please complete all required information above in order to continue';
    titles = TITLES;
    countries = COUNTRIES;
    referralSources = [
        'Google Search',
        'Social Media (Facebook, Instagram, etc.)',
        'Friend or Family Recommendation',
        'Doctor Referral',
        'Website',
        'Advertisement',
        'Previous Patient',
        'Other'
    ];
    inviteToken?: string;
    medicalAidSchemes: SchemeEntry[] = [];
    private _medicalAidInitial?: MedicalAidPickerInitial | null;
    showCompleted: boolean = false;
    submissionError: boolean = false;
    submissionErrorMessage: string = '';
    inviteName: string = '';
    mainSubmissionComplete: boolean = false;
    private handleBeforeUnload?: (event: BeforeUnloadEvent) => string | undefined;
    group?: any;
    inviteDetails?: any;
    consentDetails?: any;
    errorText: string = '';
    errorHeading: string = 'Something Went Wrong';
    inviteStatus: string | null = null;
    canRetryInviteLoad: boolean = true;
    alreadySubmitted: boolean = false;
    consentOnly: boolean = false;

    /**
     * A DOCUMENT-ONLY invite — `tasks/practice-documents.md` §21.2, the analogue of `consentOnly`.
     *
     * The wizard shows `welcome` and the spliced document steps AND NOTHING ELSE: no consent step
     * (consent's is hardcoded and would render vacuously — §20.1), no signature step (a document
     * return is a file upload and there is nothing to sign), no practice-form steps, and none of
     * personal / contact / account / medical / emergency.
     *
     * This is what makes the manual send from the patient file survive the retirement of
     * `/patient-document/<token>`: the send mints one of these the way `createConsentInvite` mints
     * a consent-only one, and the patient gets a wizard containing exactly what they were asked
     * for.
     */
    documentOnly: boolean = false;

    privatePatient: boolean = false;
    isSubmitting: boolean = false;
    submissionOverlayMessage = '';
    submissionOverlaySubtext = '';
    
    // Local storage persistence
    private readonly STORAGE_KEY_PREFIX = 'patient_invite_wizard_';
    private hasRestoredData: boolean = false;
    
    // Retry mechanism
    private submissionRetryCount: number = 0;
    private readonly MAX_RETRY_ATTEMPTS: number = 1;

    // Wizard-specific properties
    currentStep: number = 0;
    totalSteps: number = 0;
    stepProgress: number = 0;
    accountHolderSame: boolean | null = null;
    idTypeSelection: string = '';
    liableIdTypeSelection: string = '';

    // Form groups for wizard steps
    personalInformationForm!: FormGroup;
    contactInformationForm!: FormGroup;
    emergencyContactForm!: FormGroup;
    medicalInsuranceForm!: FormGroup;
    personLiableForm!: FormGroup;
    consentForms!: FormArray;
    signatureForm!: FormGroup;
    allergyForm!: FormGroup;
    medicalHistoryForm!: FormGroup;
    medicationHistoryForm!: FormGroup;
    medicalTeamForm!: FormGroup;
    referralSourceForm!: FormGroup;

    // Wizard step data
    wizardSteps: any[] = [];

    /* ── Practice forms (§6, §9 phase 3) ──────────────────────────────────────────────────────
     *
     * The forms ride INSIDE this invite: `loadInviteById` returns them with their pinned schema
     * and the single `PUT /patient-invites/:id` below files the answers. There is no second
     * endpoint, no second token and no second link for the patient to lose.
     *
     * ── NO SERVER-SIDE DRAFT HERE, AND THAT IS A KNOWN ASYMMETRY (§14.4) ──────────────────────
     *
     * The standalone `/patient-form/:token` page autosaves each keystroke to the server, so a
     * patient can start a ten-page form on the phone that got the SMS and finish it on a laptop.
     * This wizard has always persisted to `localStorage` instead, and the form answers ride that
     * same mechanism — so a refresh keeps them and a change of device does not. Accepted for v1
     * rather than built twice: a second, invite-only draft endpoint would be a second thing that
     * can disagree with the request row about what the patient has answered. If it ever matters,
     * the fix is the wizard adopting the existing PATCH, not a new mechanism.
     */
    formRequests: InviteFormRequest[] = [];

    /* ── Practice documents (`tasks/practice-documents.md` §19) ───────────────────────────────
     *
     * EMPTY IS THE ORDINARY CASE, and it must be INVISIBLE. There is no `*ngIf` guard anywhere
     * below saying "only if there are documents": zero rows here produce zero steps out of
     * `applyDocumentSteps()`, so the step does not exist, the `*ngFor` draws nothing, no timer is
     * started and no endpoint is called. Structure, not a flag — the consent step's hardcoded
     * entry is exactly the shape being avoided, because it renders an empty step when a patient
     * has nothing to consent to.
     */
    documentRequests: InviteDocumentRequest[] = [];

    /**
     * What the document file picker offers.
     *
     * From `core/services/patient-document-copy.ts`, which is where this and the refusal
     * vocabulary moved when the standalone page's service was deleted (§19). Kept no looser than
     * the server's own signature list — a client that accepts what the server refuses has already
     * cost the upload.
     */
    readonly acceptedReturnTypes = ACCEPTED_RETURN_TYPES;

    /** Said when a required document has not been returned and the patient presses Next. */
    readonly outstandingDocumentMessage =
        'Please send the completed document back before continuing';

    /**
     * The floor and ceiling on the blank URL's re-mint cadence.
     *
     * The interval itself is `expiresInMinutes / 2`, FROM THE SERVER — it is the only party that
     * knows how long the URL it just signed is good for, and a hardcoded interval silently becomes
     * wrong the day `resolveSasMinutes` is retuned. These two only stop a pathological value from
     * turning into a request storm or into no refresh at all.
     */
    readonly minBlankRefreshMs = 30 * 1000;
    readonly maxBlankRefreshMs = 10 * 60 * 1000;

    /** How often the timer wakes. Each tick decides whether the URL on screen is DUE. */
    readonly blankRefreshTickMs = 15 * 1000;

    /**
     * The re-mint timer, started only when this invite actually carries documents.
     *
     * Cleared in `ngOnDestroy`. An invite with no documents never starts one at all — see
     * `documentRequests` above.
     */
    private blankRefreshSub?: Subscription;

    /** The in-flight return, per document, so navigating away and back does not orphan it. */
    private documentUploadSubs = new Map<string, Subscription>();

    /**
     * The renderers, in `formRequests` order — the `*ngFor` in the template is the only thing that
     * creates them, so index i belongs to `formRequests[i]`.
     *
     * They are kept in the DOM and merely HIDDEN when the patient is on another step. Destroying
     * one with `*ngIf` would take its controls with it, and with them the answers to every section
     * of that form the patient has already filled in.
     */
    @ViewChildren(DynamicFormRendererComponent) formRenderers!: QueryList<DynamicFormRendererComponent>;

    stepperOrientation!: Observable<StepperOrientation>;
    isMobile!: boolean;

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private patientInviteService: PatientInviteService,
        private cd: ChangeDetectorRef,
        private scrollService: ScrollPositionService,
        private fileDownloadService: FileDownloadService,
        breakpointObserver: BreakpointObserver,
        fs: FeedbackService,
        md: MatDialog
    ) {
        super(fs, md);

        this.stepperOrientation = breakpointObserver
            .observe('(min-width: 800px)')
            .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));

        breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.TabletPortrait])
            .subscribe(result => {
                this.isMobile = result.matches;
            });
    }

    async ngOnInit() {
        this.initForms();
        this.setupChangeListeners();

        this.route.params.subscribe(async params => {
            this.inviteToken = params['token'];
            
            this.loadInviteDetails();
        });

        // Add beforeunload event listener to warn users when closing tab
        this.setupBeforeUnloadWarning();
    }

    ngAfterViewInit() {
        // Remove darkMode class from body
        this.removeDarkMode();
    }

    override ngOnDestroy() {
        // Remove beforeunload event listener
        if (this.handleBeforeUnload) {
            window.removeEventListener('beforeunload', this.handleBeforeUnload);
        }
        // The document blank re-mint timer. It only exists on an invite carrying documents, and
        // leaving it running would keep re-minting signed URLs for a page nobody is looking at.
        this.blankRefreshSub?.unsubscribe();
        this.blankRefreshSub = undefined;
        super.ngOnDestroy();
    }

    private removeDarkMode() {
        setTimeout(() => {
            const body = document.body;
            body.classList.remove('darkMode');
            body.classList.add('lightMode');
            const main = document.querySelector('main');
            if (main) {
                main.classList.add('inviteWizard');
            }
        }, 1000);
    }

    private setupBeforeUnloadWarning() {

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (this.isSubmitting) {
                const message = 'Your submission is in progress. Leaving now may interrupt it.';
                event.preventDefault();
                event.returnValue = message;
                return message;
            }
            if (this.currentStep > 0 && !this.showCompleted) {
                const message = 'You have unsaved changes. Are you sure you want to leave? Your progress may be lost.';
                event.preventDefault();
                event.returnValue = message;
                return message;
            }
            return undefined;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Store the handler reference for cleanup
        this.handleBeforeUnload = handleBeforeUnload;
    }



    private loadInviteDetails() {
        const sub = this.patientInviteService.validateInvite(this.inviteToken!)
            .pipe(
                takeUntil(this.destroy$),
                switchMap(result => {
                    if (result.data) {
                        // By the TOKEN, not `result.data.id`: the invite's id alone must not be enough to read
                        // the patient's details (tasks/patient-app.md §10.1). The endpoint matches either.
                        return this.patientInviteService.getInviteById(this.inviteToken!);
                    } else {
                        throw new Error('No invite data available');
                    }
                })
            )
            .subscribe({
                next: async (result) => {
                    this.inviteDetails = result.data;
                    this.group = this.inviteDetails.group;
                    this.inviteName = this.inviteDetails.Patient.name;
                    this.consentOnly = this.inviteDetails.consentOnly;
                    // §21.2. Read BEFORE `consumeFormRequests`, which drops practice forms on a
                    // document-only invite — "nothing else" includes them.
                    this.documentOnly = !this.consentOnly && !!this.inviteDetails.documentOnly;
                    this.consentDetails = this.inviteDetails.Patient.PatientConsents || [];

                    this.loadMedicalAidSchemes();
                    this.consumeFormRequests(this.inviteDetails.formRequests);
                    this.consumeDocumentRequests(this.inviteDetails.documentRequests);
                    this.createConsentForms(this.consentDetails);
                    this.initializeWizardSteps();

                    // If nextOfKin is enforced, automatically set hasEmergencyContact to true
                    if (this.group?.enforcedInviteSections?.nextOfKin === true) {
                        this.emergencyContactForm.patchValue({ hasEmergencyContact: true });
                    }

                    // Set form values
                    this.personalInformationForm.patchValue({
                        id: this.inviteDetails.id,
                        consentOnly: this.consentOnly,
                        patientId: this.inviteDetails.patientId,
                        groupId: this.inviteDetails.groupId,
                        name: this.inviteDetails.Patient.name,
                        surname: this.inviteDetails.Patient.surname
                    });
                    this.contactInformationForm.patchValue({
                        email: this.inviteDetails.Patient.email,
                        mobileNumber: this.inviteDetails.Patient.mobileNumber
                    });

                    if (this.consentOnly || this.documentOnly) {
                        // None of those sections is on screen in either mode, so a validator on a
                        // control the patient can never reach is only a way for submit to refuse
                        // with a message about a step that does not exist.
                        this.clearFormValidators();
                    }

                    this.cd.detectChanges();
                    
                    // Check for stored data only after successful token validation
                    await this.checkForStoredData();
                    
                    sub.unsubscribe();
                },
                error: (error) => {
                    console.error(error);
                    this.applyInviteLoadFailure(error);
                    sub.unsubscribe();
                }
            });
    }

    /**
     * The validate endpoint distinguishes a completed invite from one that is expired, revoked or
     * unknown. Previously every one of these surfaced as the same "not valid or expired" error, so a
     * patient who simply re-opened their link after finishing was told to phone the practice.
     */
    private applyInviteLoadFailure(error: any): void {
        this.inviteStatus = error?.error?.status ?? null;
        const serverMessage = error?.error?.message;

        switch (this.inviteStatus) {
            case 'completed':
                this.errorHeading = 'All Done';
                this.errorText = serverMessage || 'You have already completed this form. There is nothing further you need to do.';
                this.canRetryInviteLoad = false;
                return;
            case 'expired':
            case 'revoked':
                this.errorHeading = 'This Link Is No Longer Active';
                this.errorText = serverMessage || 'Please contact the practice to request a new link.';
                this.canRetryInviteLoad = false;
                break;
            case 'not_found':
                this.errorHeading = 'Invitation Not Found';
                this.errorText = serverMessage || 'Please check the link, or contact the practice to request a new one.';
                this.canRetryInviteLoad = false;
                break;
            default:
                this.errorHeading = 'Something Went Wrong';
                this.errorText = `Failed to load invite: ${serverMessage || 'An unknown error occurred.'}`;
                this.canRetryInviteLoad = true;
        }

        this.showToastMessage(this.errorText);
    }

    /**
     * The three hardcoded step lists — `tasks/practice-documents.md` §21.2.
     *
     * `consentOnly`  → welcome, consent, signature
     * `documentOnly` → welcome, and then ONLY what `applyDocumentSteps()` splices in
     * otherwise     → the historic eight
     *
     * The document-only list is one entry long on purpose. Everything the patient has to do is a
     * spliced step, so an invite carrying two documents is three steps and an invite carrying none
     * is one — which is the degenerate case the Finish button on the welcome step exists for (see
     * `showsFinishButton`).
     */
    private initializeWizardSteps() {
        if (this.documentOnly) {
            this.wizardSteps = [
                { id: 'welcome', title: 'Welcome', icon: 'home', completed: false },
            ];
        } else if (this.consentOnly) {
            this.wizardSteps = [
                { id: 'welcome', title: 'Welcome', icon: 'home', completed: false },
                { id: 'consent', title: 'Consent Forms', icon: 'description', completed: false },
                { id: 'signature', title: 'Signature', icon: 'edit', completed: false }
            ];
        } else {
            this.wizardSteps = [
                { id: 'welcome', title: 'Welcome', icon: 'home', completed: false },
                { id: 'personal', title: 'Personal Info', icon: 'person', completed: false },
                { id: 'contact', title: 'Contact Info', icon: 'contact_phone', completed: false },
                { id: 'account', title: 'Medical Aid & Account', icon: 'account_balance', completed: false },
                { id: 'medical', title: 'Medical Details', icon: 'medical_services', completed: false },
                { id: 'emergency', title: 'Emergency Contact', icon: 'emergency', completed: false },
                { id: 'consent', title: 'Consent Forms', icon: 'description', completed: false },
                { id: 'signature', title: 'Signature', icon: 'edit', completed: false }
            ];
        }
        this.applyFormSteps();
        this.applyDocumentSteps();
    }

    /* ══════════════════════════════════════════════════════════════════════════════════════════
     * Practice forms — one wizard step per form SECTION (§6, §14.2)
     * ════════════════════════════════════════════════════════════════════════════════════════ */

    /**
     * Adopt what `loadInviteById` returned. Anything without a schema is dropped, not rendered.
     *
     * A DOCUMENT-ONLY invite adopts NONE of them (§21.2 — "only the documents"). Dropping them
     * here rather than hiding them later is what makes the claim structural: no step is spliced,
     * no renderer is created, and `submit()` posts an empty `forms` array, so there is no path by
     * which a form the patient never saw is answered on their behalf.
     */
    private consumeFormRequests(payload: any): void {
        const rows = this.documentOnly ? [] : (Array.isArray(payload) ? payload : []);
        this.formRequests = rows
            .filter((row: any) => row && row.id && row.schema)
            .map((row: any) => ({
                id: row.id,
                name: row.name || 'Form',
                description: row.description || null,
                requiresSignature: !!row.requiresSignature,
                schema: row.schema as FormSchema,
                answers: (row.answers && typeof row.answers === 'object') ? row.answers as FormAnswers : {},
                sections: [],
                activeSectionId: null,
            }));
    }

    /**
     * Splice one step per visible form section between `consent` and `signature`.
     *
     * ── WHY THIS IS RE-RUN RATHER THAN COMPUTED ONCE ──────────────────────────────────────────
     *
     * The section list is not a property of the schema, it is a property of the ANSWERS: a whole
     * section can vanish behind a `showIf` as the patient types, and a step with nothing on it is
     * a dead end. So the renderer tells us (`sectionsChange`) and this runs again — which means
     * the patient can be standing on a step that no longer exists. They are re-homed onto the
     * nearest surviving step rather than left on an index that now points at something else, the
     * same problem the standalone page solves in `goToSection` (§14.4 decision 5).
     *
     * The signature step stays LAST, which is what `retrySubmission()` and the submit button rely
     * on, and the base steps keep their ids so `isStepValid` is unaffected by how many forms a
     * practice attaches.
     */
    private applyFormSteps(): void {
        const standingOn = this.wizardSteps[this.currentStep]?.id ?? null;

        const base = this.wizardSteps.filter((step) => step.type !== 'form');
        const formSteps: any[] = [];
        for (const request of this.formRequests) {
            for (const section of request.sections) {
                const id = `form:${request.id}:${section.id}`;
                const existing = this.wizardSteps.find((step) => step.id === id);
                formSteps.push({
                    id,
                    type: 'form',
                    title: section.title || request.name,
                    icon: 'assignment',
                    completed: existing ? existing.completed : false,
                    formRequestId: request.id,
                    sectionId: section.id,
                });
            }
        }

        // Before `signature` — and before the first DOCUMENT step, which sits between the two
        // (§19). `base` still holds any document steps, because this rebuild only ever removes and
        // re-creates the form ones; landing forms after them would reorder the wizard on the next
        // `sectionsChange` and move a step out from under a patient standing on it.
        const signatureIndex = base.findIndex((step) => step.id === 'signature' || step.type === 'document');
        const insertAt = signatureIndex >= 0 ? signatureIndex : base.length;
        this.wizardSteps = [...base.slice(0, insertAt), ...formSteps, ...base.slice(insertAt)];
        this.totalSteps = this.wizardSteps.length;

        const found = standingOn === null ? -1 : this.wizardSteps.findIndex((step) => step.id === standingOn);
        this.currentStep = found >= 0
            ? found
            : Math.max(0, Math.min(this.currentStep, this.totalSteps - 1));

        this.syncFormPaging();
        this.updateProgress();
    }

    /**
     * Point every form's renderer at the section its step is showing.
     *
     * A renderer that is not on screen keeps whatever section it last drew — it is hidden, so what
     * it draws does not matter, and leaving `sectionId` null there would make it render the WHOLE
     * form invisibly, which is a needless pile of controls and a needless recompute on every
     * keystroke elsewhere. A form whose section list has not arrived yet pages to nothing.
     */
    private syncFormPaging(): void {
        const current = this.wizardSteps[this.currentStep];
        for (const request of this.formRequests) {
            if (current && current.type === 'form' && current.formRequestId === request.id) {
                request.activeSectionId = current.sectionId;
            } else if (request.activeSectionId === null && request.sections.length > 0) {
                request.activeSectionId = request.sections[0].id;
            }
        }
    }

    /** `sectionsChange` (§14.2) — the ONLY source of truth for how many steps a form contributes. */
    onFormSections(request: InviteFormRequest, sections: FormSection[]): void {
        request.sections = Array.isArray(sections) ? sections : [];
        this.applyFormSteps();
        this.updateStepCompletionStatus();
    }

    /** The renderer owns the answers, including the prune; this only holds and posts them. */
    onFormAnswers(request: InviteFormRequest, answers: FormAnswers): void {
        request.answers = answers || {};
        this.updateStepCompletionStatus();
        this.autoSaveFormData();
    }

    /** The renderer instance drawing `formRequests[i]`, or undefined before the view exists. */
    private rendererFor(request: InviteFormRequest): DynamicFormRendererComponent | undefined {
        if (!this.formRenderers) return undefined;
        const index = this.formRequests.indexOf(request);
        return index < 0 ? undefined : this.formRenderers.toArray()[index];
    }

    private formRequestById(id: string): InviteFormRequest | undefined {
        return this.formRequests.find((request) => request.id === id);
    }

    /** Whether this form's step is the one on screen. Drives `[hidden]`, never `*ngIf`. */
    isFormStepVisible(request: InviteFormRequest): boolean {
        const step = this.wizardSteps[this.currentStep];
        return !!step && step.type === 'form' && step.formRequestId === request.id;
    }

    trackFormRequest(_index: number, request: InviteFormRequest): string {
        return request.id;
    }

    /* ══════════════════════════════════════════════════════════════════════════════════════════
     * Practice documents — one wizard step per document (`tasks/practice-documents.md` §19)
     *
     * ── THE STEP IS INVISIBLE WHEN THERE ARE NO DOCUMENTS, AND THAT IS STRUCTURAL ─────────────
     *
     * `applyDocumentSteps()` splices one step per row of `documentRequests`. Zero rows, zero
     * steps: there is nothing to hide because there is nothing to draw, no `*ngIf` emptiness
     * guard anywhere, and no timer or endpoint touched on the overwhelmingly common invite that
     * carries no documents at all. This is why the FORM steps were mirrored here and the CONSENT
     * step deliberately was not — consent's step is hardcoded into `initializeWizardSteps()` and
     * renders an empty screen when a patient has nothing outstanding.
     * ════════════════════════════════════════════════════════════════════════════════════════ */

    /** Adopt what `loadInviteById` returned. A row without an id is dropped, not drawn. */
    private consumeDocumentRequests(payload: any): void {
        const rows: InviteDocumentRequestPayload[] = Array.isArray(payload) ? payload : [];
        this.documentRequests = rows
            .filter((row: any) => row && row.id)
            .map((row) => {
                const limits = row.upload || {};
                return {
                    id: String(row.id),
                    name: row.name || 'Document',
                    description: row.description || null,
                    pageCount: typeof row.pageCount === 'number' ? row.pageCount : null,
                    fileName: row.fileName || null,
                    requiredInInvite: !!row.requiredInInvite,
                    // WHITELISTED, not defaulted. See `readFillMode` — anything that is not
                    // literally 'embedded' is the download path.
                    fillMode: readFillMode(row.fillMode),
                    embeddedUnavailableReason: null,
                    // The caps are the SERVER'S. A cap this client invented could drift LOOSER than
                    // the real one, which is the failure that costs the upload before the refusal
                    // (§10.2); a cap the payload did not carry is therefore not enforced at all.
                    maxFiles: typeof limits.maxFiles === 'number' && limits.maxFiles > 0 ? limits.maxFiles : null,
                    maxTotalBytes: typeof limits.maxTotalBytes === 'number' && limits.maxTotalBytes > 0
                        ? limits.maxTotalBytes : null,
                    accepts: limits.accepts || null,
                    // ABSENT MEANS AN ORDINARY ASK. A server that has not shipped the
                    // resubmission fields sends neither, and this step then renders exactly as it
                    // always did.
                    resubmission: row.resubmission === true,
                    previousNote: row.previousNote || null,
                    previousFileCount: typeof row.previousFileCount === 'number'
                        ? row.previousFileCount : 0,
                    previousFiles: [],
                    previousLoading: false,
                    previousError: null,
                    previousOpen: false,
                    blankUrl: null,
                    blankExpiresInMinutes: null,
                    blankMintedAt: 0,
                    blankLoading: false,
                    blankError: null,
                    files: [],
                    rejectedFileNames: [],
                    pickerObjects: [],
                    limitError: null,
                    isUploading: false,
                    uploadPercent: null,
                    uploadError: null,
                    returned: false,
                    returnedMessage: '',
                } as InviteDocumentRequest;
            });

        this.startBlankRefreshTimer();
    }

    /**
     * Splice one step per document, between the practice-form steps and `signature`.
     *
     * The same shape as `applyFormSteps()`, including the re-home: a patient standing on a step
     * that no longer exists is moved onto the nearest surviving one rather than left on an index
     * that now points at something else.
     */
    private applyDocumentSteps(): void {
        const standingOn = this.wizardSteps[this.currentStep]?.id ?? null;

        const base = this.wizardSteps.filter((step) => step.type !== 'document');
        const documentSteps = this.documentRequests.map((request) => {
            const id = `document:${request.id}`;
            const existing = this.wizardSteps.find((step) => step.id === id);
            return {
                id,
                type: 'document',
                title: request.name,
                icon: 'description',
                completed: existing ? existing.completed : false,
                documentRequestId: request.id,
            };
        });

        const signatureIndex = base.findIndex((step) => step.id === 'signature');
        const insertAt = signatureIndex >= 0 ? signatureIndex : base.length;
        this.wizardSteps = [...base.slice(0, insertAt), ...documentSteps, ...base.slice(insertAt)];
        this.totalSteps = this.wizardSteps.length;

        const found = standingOn === null ? -1 : this.wizardSteps.findIndex((step) => step.id === standingOn);
        this.currentStep = found >= 0
            ? found
            : Math.max(0, Math.min(this.currentStep, this.totalSteps - 1));

        this.syncDocumentPaging();
        this.updateProgress();
    }

    /* ══════════════════════════════════════════════════════════════════════════════════════════
     * SAYING WHAT IS NEEDED BEFORE THE PATIENT STARTS — `tasks/practice-documents.md` §5
     *
     * §5 names the cost of the hard block and then asks for one thing about it:
     *
     *   > the step's copy should say plainly what is needed *before* the patient starts the
     *   > wizard, not at step 7 of 8.
     *
     * IT DID NOT, AND THAT WAS THE STATE UNTIL PHASE 5. The welcome step had three mutually
     * exclusive paragraphs — the ordinary invite, `consentOnly`, and `documentOnly` — and the
     * FIRST of them, which is the eight-step wizard §5 is actually describing, never mentioned
     * documents at all. A patient on an ordinary invite met a required document for the first
     * time on the step that would not let them past it, which is the exact sentence above.
     *
     * ── WHY THIS BELONGS TO PHASE 5 RATHER THAN TO PHASE 3 ───────────────────────────────────
     *
     * Because the honest answer changed. Before tier A, every hard block had one consequence —
     * find a printer or come back later — and the notice would have been one sentence. Now it
     * depends on the tier: a required document that fills in on screen costs a patient nothing at
     * 9pm with no printer, and one that does not costs them the evening. A notice that told them
     * the wrong one would be worse than none, so the two are said separately below.
     *
     * ── 11 SEP 2026: IT ANNOUNCED TOO LITTLE, NOT TOO MUCH ───────────────────────────────────
     *
     * Phase 5 wired the notice to `requiredInInvite`, which is off by default. Reported by the
     * owner as "it shows in some cases and not in others": an invite carrying a document nobody
     * had marked required said nothing on the welcome step, which is the majority of sends and
     * the exact §5 complaint the notice was built to answer.
     *
     * The list is now everything OUTSTANDING. The hard block did not widen with it - the
     * "cannot be completed until" sentence still names only `requiredInInvite` documents, and on
     * a mixed invite it names WHICH ones, because a notice that told a patient an optional
     * leaflet would hold up their appointment would be a different kind of false.
     * ════════════════════════════════════════════════════════════════════════════════════════ */

    /**
     * WHAT IS STILL OUTSTANDING - every document this invite is waiting on, blocking or not.
     *
     * `requiredDocuments()` used to be the only list here, which is why the notice showed on some
     * invites and not others: `requiredInInvite` is OFF BY DEFAULT, so the overwhelmingly common
     * send - a practice that attached a form and never touched the setting - announced nothing at
     * all and the patient met the document for the first time on its own step. §5's sentence is
     * about telling them what is needed BEFORE they start, and that is true of every attached
     * document; only the CONSEQUENCE of ignoring one depends on the flag.
     */
    outstandingDocuments(): InviteDocumentRequest[] {
        return this.documentRequests.filter((request) => !request.returned);
    }

    /** The subset that will actually hold the invite up. Drives the "cannot be completed" line. */
    blockingDocuments(): InviteDocumentRequest[] {
        return this.outstandingDocuments().filter((request) => request.requiredInInvite);
    }

    /** Whether the hard block covers everything outstanding, so the line can say "them". */
    everyOutstandingDocumentBlocks(): boolean {
        const outstanding = this.outstandingDocuments();
        return outstanding.length > 0 && outstanding.every((request) => request.requiredInInvite);
    }

    /** "the SAQ", "the SAQ and the COID form", "A, B and C" - a sentence, not a list widget. */
    outstandingDocumentNames(): string {
        return this.namesSentence(this.outstandingDocuments());
    }

    /** The same sentence over the blocking subset, for the mixed invite that has to name them. */
    blockingDocumentNames(): string {
        return this.namesSentence(this.blockingDocuments());
    }

    private namesSentence(requests: InviteDocumentRequest[]): string {
        const names = requests.map((request) => request.name);
        if (names.length === 0) return '';
        if (names.length === 1) return names[0];
        return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    }

    /**
     * Whether EVERY outstanding document can be completed on the phone the patient is holding.
     *
     * ALL, not SOME, and deliberately so: one printable document among three on-screen ones still
     * means a patient with no printer cannot finish, and a notice that led with "you can do all
     * of this here" would be false in exactly the case that matters.
     *
     * It reads the OUTSTANDING list rather than the blocking one because the printer is needed
     * either way - an optional document that has to be printed costs the patient the same evening
     * as a required one, and the promise "no printer needed" would be false for both.
     */
    everyOutstandingDocumentFillsOnScreen(): boolean {
        const outstanding = this.outstandingDocuments();
        return outstanding.length > 0
            && outstanding.every((request) => request.fillMode === EMBEDDED_FILL_MODE);
    }


    /** Whether this document's step is the one on screen. Drives `[hidden]`, never `*ngIf`. */
    isDocumentStepVisible(request: InviteDocumentRequest): boolean {
        const step = this.wizardSteps[this.currentStep];
        return !!step && step.type === 'document' && step.documentRequestId === request.id;
    }

    trackDocumentRequest(_index: number, request: InviteDocumentRequest): string {
        return request.id;
    }

    private documentRequestById(id: string): InviteDocumentRequest | undefined {
        return this.documentRequests.find((request) => request.id === id);
    }

    /** The document whose step is on screen, or undefined — the "am I live?" gate for the timer. */
    private visibleDocumentRequest(): InviteDocumentRequest | undefined {
        const step = this.wizardSteps[this.currentStep];
        if (!step || step.type !== 'document') return undefined;
        return this.documentRequestById(step.documentRequestId);
    }

    /**
     * The step on screen has changed. Mint the blank's URL for the document that is now showing.
     *
     * MINTED ON ARRIVAL, not on load: a signed URL minted at the top of the wizard would be minutes
     * dead by the time a patient reached this step, and every invite carrying a document would pay
     * for a SAS the patient may never scroll to.
     */
    private syncDocumentPaging(): void {
        const request = this.visibleDocumentRequest();
        if (request) this.ensureDocumentBlank(request);
    }

    /* ── The blank, and keeping its URL alive ───────────────────────────────────────────────── */

    /**
     * How long the URL on screen is good for, on the SERVER'S clock, halved.
     *
     * Halving `expiresInMinutes` means a re-mint always lands well before the link the patient is
     * looking at goes dead, and this component never has an opinion about SAS lifetimes of its own.
     */
    private blankRefreshIntervalMs(request: InviteDocumentRequest): number {
        const minutes = request.blankExpiresInMinutes;
        if (!minutes || minutes <= 0) return this.maxBlankRefreshMs;
        const half = (minutes * 60 * 1000) / 2;
        return Math.min(this.maxBlankRefreshMs, Math.max(this.minBlankRefreshMs, half));
    }

    /**
     * Start the re-mint timer — ONLY on an invite that actually carries documents.
     *
     * A SHORT TICK THAT ASKS "IS IT DUE?", not a long interval set to the lifetime: the lifetime is
     * only known once the first mint answers, and re-arming an interval from inside itself is how a
     * timer ends up with two subscriptions.
     */
    private startBlankRefreshTimer(): void {
        if (!this.documentRequests.length || this.blankRefreshSub) return;
        this.blankRefreshSub = interval(this.blankRefreshTickMs)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.refreshDocumentBlankIfDue());
    }

    /**
     * Called on every tick. Re-mints only the document whose step is ON SCREEN, and only once the
     * URL it is showing is halfway through its life.
     *
     * THE OFF-SCREEN CASE IS THE WHOLE POINT OF THE GATE: a patient sitting on the signature step
     * for twenty minutes must not be minting signed URLs for documents nobody is looking at, and a
     * patient standing at a printer with this step open must not come back to a dead one.
     */
    refreshDocumentBlankIfDue(): void {
        const request = this.visibleDocumentRequest();
        if (!request || !request.blankMintedAt) return;
        if (Date.now() - request.blankMintedAt < this.blankRefreshIntervalMs(request)) return;
        this.refreshDocumentBlank(request);
    }

    /** First mint for a document arriving on screen. A re-mint goes through `refreshDocumentBlank`. */
    private ensureDocumentBlank(request: InviteDocumentRequest): void {
        if (request.blankMintedAt || request.blankLoading || request.returned) return;
        this.mintDocumentBlank(request);
    }

    /**
     * Re-mint the URL for a document already on screen.
     *
     * A failure is SWALLOWED onto `blankError` without disturbing the URL already showing — it may
     * still work, and replacing a working download with an error because a background refresh timed
     * out would be strictly worse than doing nothing.
     */
    private refreshDocumentBlank(request: InviteDocumentRequest): void {
        if (request.blankLoading || request.isUploading || request.returned) return;
        this.mintDocumentBlank(request);
    }

    private mintDocumentBlank(request: InviteDocumentRequest): void {
        const inviteId = this.inviteRowId;
        const shortToken = this.inviteShortToken;
        if (!inviteId || !shortToken) return;

        request.blankLoading = true;
        this.patientInviteService.getDocumentBlankUrl(inviteId, request.id, shortToken)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (blank) => {
                    request.blankLoading = false;
                    if (blank && blank.url) {
                        request.blankUrl = blank.url;
                        request.blankExpiresInMinutes = blank.expiresInMinutes;
                        request.blankMintedAt = Date.now();
                        if (blank.fileName) request.fileName = blank.fileName;
                        if (typeof blank.pageCount === 'number') request.pageCount = blank.pageCount;
                        request.blankError = null;
                    }
                    this.cd.detectChanges();
                },
                error: () => {
                    request.blankLoading = false;
                    // The mint clock is reset even on a failure, so a server that is down produces
                    // one attempt per interval rather than one per tick.
                    request.blankMintedAt = Date.now();
                    if (!request.blankUrl) {
                        request.blankError = 'We could not open this document just now.'
                            + ' Please check your connection and try again.';
                    }
                    this.cd.detectChanges();
                },
            });
    }

    /* ═══════════════════════════════════════════════════════════════════
     * WHAT THEY SENT LAST TIME — `tasks/document-rejection-resend.md`
     * ═════════════════════════════════════════════════════════════════ */

    /**
     * Show or hide the previous attempt, fetching it the first time.
     *
     * ON DEMAND, NOT ON LOAD. The links expire in minutes and most patients will not ask — they
     * have just read what needs to change and will go and do it. Minting a set of signed URLs for
     * every resubmission step on every wizard load would be work nobody asked for, over blobs
     * nobody opens.
     *
     * A FAILURE HERE IS NOT A FAILURE OF THE STEP. Being unable to show somebody what they sent
     * last time does not stop them sending it again, so the error is inline and the rest of the
     * step is untouched.
     */
    togglePreviousAttempt(request: InviteDocumentRequest): void {
        if (request.previousOpen) {
            request.previousOpen = false;
            return;
        }
        request.previousOpen = true;

        // Already fetched, and the links are still the ones we were given. Re-minting on every
        // toggle would spend a round trip to redraw the same thumbnails.
        if (request.previousFiles.length || request.previousLoading) return;

        const inviteId = this.inviteRowId;
        const shortToken = this.inviteShortToken;
        if (!inviteId || !shortToken) return;

        request.previousLoading = true;
        request.previousError = null;
        this.patientInviteService.getDocumentPreviousFiles(inviteId, request.id, shortToken)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (previous) => {
                    request.previousLoading = false;
                    request.previousFiles = (previous && previous.files) || [];
                    this.cd.detectChanges();
                },
                error: () => {
                    request.previousLoading = false;
                    request.previousError = 'We could not open what you sent us last time.'
                        + ' You can still send the document again below.';
                    this.cd.detectChanges();
                },
            });
    }

    /** A page whose stored reference could not be signed. Reported, never silently dropped. */
    previousFileLabel(file: InviteDocumentPreviousFile, index: number): string {
        return file.fileName || `Page ${index + 1}`;
    }

    /** Ask for the blank again after a failure. The only retry affordance on this step. */
    retryDocumentBlank(request: InviteDocumentRequest): void {
        request.blankMintedAt = 0;
        request.blankError = null;
        this.ensureDocumentBlank(request);
    }

    private get inviteRowId(): string {
        return this.inviteDetails?.id ? String(this.inviteDetails.id) : '';
    }

    /** The invite's own short token — the same one `submit()` appends to its `FormData`. */
    private get inviteShortToken(): string {
        return this.inviteToken ?? this.inviteDetails?.shortToken ?? '';
    }

    /* ══════════════════════════════════════════════════════════════════════════════════════════
     * TIER A — FILLING IT IN ON SCREEN (`tasks/practice-documents.md` §1.1, §5, Phase 5)
     *
     * ── THE VIEWER IS ADDED. NOTHING IS TAKEN AWAY. ───────────────────────────────────────────
     *
     * A document whose `fillMode` is `'embedded'` gets a "Fill it in on screen" button ABOVE the
     * download link. Everything tier C does — the blank's link, the file picker, the file list,
     * the caps, the Send button, the progress bar, the refusals — is untouched and still on the
     * page for the same document. Three things follow from that, and all three are the point:
     *
     *   · A `'download'` document is byte-for-byte what it was before Phase 5. The only new
     *     branch is `*ngIf="canFillOnScreen(...)"`, and it is false for every one of them.
     *   · §11's hazard does not arise. That pin exists so a patient who downloaded a PDF to fill
     *     in on paper cannot come back to a page that INSISTS they fill it in on screen. This
     *     page never insists — the paper path is exactly where they left it.
     *   · The fallback is structural rather than an error handler. When the viewer cannot open
     *     the document, withdrawing the button leaves a working step behind, because the working
     *     step was never replaced.
     *
     * ── THE FILLED PDF GOES BACK THROUGH THE TIER-C ENDPOINT ──────────────────────────────────
     *
     * `POST /patient-invites/:id/documents/:requestId/files`, the same multipart return, with the
     * same claim, the same caps and the same refusals. A filled PDF is a PDF: the server sniffs
     * `%PDF-` off its first bytes and files it as one media row exactly as it files a scan. A
     * second return path would be a second thing to keep in step with the claim, the media rows,
     * the communication record and the review screen — and there is no fact about a filled PDF
     * that the existing one cannot carry.
     * ════════════════════════════════════════════════════════════════════════════════════════ */

    /**
     * Whether to offer the on-screen viewer for this document.
     *
     * FOUR facts, and every one of them can withdraw it:
     *   · the server resolved this document to tier A;
     *   · the viewer has not already failed on it (`embeddedUnavailableReason`);
     *   · the blank's short-lived URL has been minted — there is nothing to open without it;
     *   · nothing has been returned yet, and no upload is in flight.
     */
    canFillOnScreen(request: InviteDocumentRequest): boolean {
        return request.fillMode === EMBEDDED_FILL_MODE
            && !request.embeddedUnavailableReason
            && !!request.blankUrl
            && !request.returned
            && !request.isUploading;
    }

    /**
     * A tier-A document whose blank has not arrived yet, so the viewer cannot be offered NOW but
     * has not been ruled out either. Drives a "preparing" line rather than a silent gap where the
     * button will appear a moment later.
     */
    isPreparingFillOnScreen(request: InviteDocumentRequest): boolean {
        return request.fillMode === EMBEDDED_FILL_MODE
            && !request.embeddedUnavailableReason
            && !request.blankUrl
            && !request.returned;
    }

    /**
     * Open the full-screen viewer.
     *
     * FULL SCREEN AND NOT CLOSE-ON-BACKDROP: this is a phone in a waiting room (§5), a ten-page
     * form takes minutes, and a stray tap outside a floating dialog that threw all of it away
     * would be unforgivable. `Escape` is disabled for the same reason — the dialog has its own
     * close button, and it is the one that runs `cancel()`.
     */
    openFillDialog(request: InviteDocumentRequest): void {
        if (!this.canFillOnScreen(request)) return;

        const data: DocumentFillDialogData = {
            documentName: request.name,
            blankUrl: request.blankUrl as string,
            fileName: request.fileName,
        };

        this.dialog.open<DocumentFillDialogComponent, DocumentFillDialogData, DocumentFillDialogResult>(
            DocumentFillDialogComponent,
            {
                data,
                panelClass: 'document-fill-dialog-panel',
                width: '100vw',
                maxWidth: '100vw',
                height: '100dvh',
                maxHeight: '100dvh',
                autoFocus: false,
                disableClose: true,
            },
        ).afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => this.applyFillDialogResult(request, result));
    }

    /**
     * What came back from the viewer.
     *
     * `undefined` is a plain cancel and changes NOTHING — the patient looked, backed out, and the
     * button is still there. The other two are the two halves of §5.
     */
    private applyFillDialogResult(
        request: InviteDocumentRequest,
        result: DocumentFillDialogResult | undefined,
    ): void {
        if (!result) return;

        if (result.status === 'unavailable') {
            // THE FALLBACK. The viewer is withdrawn for the rest of this step and the download
            // path — which has been on the page the whole time — is now the only one offered.
            request.embeddedUnavailableReason = result.reason;
            this.cd.detectChanges();
            return;
        }

        this.sendFilledDocument(request, result.file);
    }

    /**
     * The filled PDF, straight down the tier-C return.
     *
     * IT REPLACES THE SELECTION rather than joining it. The patient has just completed the whole
     * document in one artefact; sending it alongside three half-photographed pages would file
     * four media rows for one return and leave a reviewer deciding which is the real one. The
     * replacement is VISIBLE — the file list under Step 2 shows exactly what is about to go — and
     * `uploadDocument` is called straight away because the patient already pressed "Send to the
     * practice" inside the dialog, and asking them to press it a second time is a step that only
     * exists because of how this is built.
     *
     * A FAILED SEND LEAVES THE FILE IN PLACE. `applyDocumentUploadFailure` never touches
     * `request.files`, so "Try again" resends the same bytes — a patient who filled in ten pages
     * on a phone does not fill them in again because a connection dropped.
     */
    private sendFilledDocument(request: InviteDocumentRequest, file: File): void {
        request.files = [file];
        request.rejectedFileNames = [];
        request.uploadError = null;
        this.checkDocumentLimits(request);
        this.updateStepCompletionStatus();
        this.cd.detectChanges();

        // A filled PDF that breaks the practice's own size cap is refused HERE, with the file
        // still on the page, rather than after the upload — the cap is the server's own number
        // and this is the same check every other return pays.
        if (request.limitError) return;

        this.uploadDocument(request);
    }

    /**
     * Why the viewer is no longer on offer, in the patient's terms.
     *
     * THREE REASONS, THREE SENTENCES (`lessons.md` §14 — collapsing facts that differ makes the
     * message useless). "That did not work" told to somebody whose document has no fillable boxes
     * makes them try again forever; told to somebody whose connection dropped, it is exactly
     * right and they should.
     */
    fillUnavailableMessage(request: InviteDocumentRequest): string {
        switch (request.embeddedUnavailableReason) {
            case 'no-fields':
                return 'This document cannot be filled in on screen — it has no boxes to type in.'
                    + ' Please download it, complete it, and send the pages back below.';
            case 'export-failed':
                return 'No problem — download the document below, complete it, and send the pages'
                    + ' back here.';
            case 'load-failed':
                return 'We could not open this document on screen. Please download it below,'
                    + ' complete it, and send the pages back here.';
            default:
                return '';
        }
    }

    /* ── Choosing the completed pages ───────────────────────────────────────────────────────── */

    /**
     * The picker handed us some files. ADD them, never replace.
     *
     * A patient photographing ten pages will open the picker several times, and a picker that
     * replaced the selection each time would silently discard the first nine.
     */
    /**
     * The drop zone handed us pages — `tasks/practice-documents.md` §5.
     *
     * `<app-file-upload>` is the house picker (drag and drop, paste, camera) and is PRESENTATIONAL:
     * it uploads nothing and injects no API service, which is what makes it safe on this page — a
     * patient on an unauthenticated token link. The practice-document settings dialogs already use
     * it, so this screen was the odd one out with a bare `<input type="file">`.
     *
     * IT EMITS `FileObject`s, WHICH HOLD AN OBJECT URL AND NOT THE FILE. The bytes are fetched back
     * out of the blob URL — the same conversion `practice-document-upload-dialog.component.ts:108`
     * already does for the blanks, lifted rather than reinvented — and the URL is revoked once it
     * has been read, because on a phone photographing ten pages these are real megabytes.
     *
     * THE POLICY STAYS HERE. `addDocumentFiles` owns the accepted-type sniff, the file and byte
     * caps and the "named, not silently dropped" refusal list. The component supplies the picker;
     * it does not get a say in what may be sent.
     *
     * A file that cannot be read is reported rather than skipped, for the same reason: a page the
     * patient believes they attached and we quietly lost is the worst outcome this step has.
     */
    async onDocumentFileObjects(request: InviteDocumentRequest, picked: FileObject[]): Promise<void> {
        const objects = Array.isArray(picked) ? picked.slice() : [];
        // Drain the component's array FIRST. It emits its whole list every time, so anything left
        // here would be handed to us again on the next pick and added twice.
        request.pickerObjects.length = 0;

        const files: File[] = [];
        const unreadable: string[] = [];

        for (const object of objects) {
            if (!object || !object.url) continue;
            try {
                const blob = await (await fetch(object.url)).blob();
                files.push(new File([blob], object.name || 'page', {
                    type: object.type || blob.type || '',
                }));
            } catch {
                unreadable.push(object.name || 'that file');
            } finally {
                URL.revokeObjectURL(object.url);
            }
        }

        this.addDocumentFiles(request, files);

        if (unreadable.length) {
            // Appended, not assigned: `addDocumentFiles` has just set the list from the sniff, and
            // a file this device could not read is refused for a different reason than a file we
            // do not accept. Both belong in front of the patient.
            request.rejectedFileNames = request.rejectedFileNames.concat(unreadable);
            this.cd.detectChanges();
        }
    }

    onDocumentFilesChosen(request: InviteDocumentRequest, event: Event): void {
        const input = event.target as HTMLInputElement | null;
        this.addDocumentFiles(request, input && input.files ? Array.prototype.slice.call(input.files) : []);
        // So choosing the SAME file again still fires `change`.
        if (input) input.value = '';
    }

    addDocumentFiles(request: InviteDocumentRequest, chosen: File[]): void {
        const accepted: File[] = [];
        const rejected: string[] = [];

        for (const file of chosen) {
            if (isAcceptedReturnFile(file)) accepted.push(file);
            else rejected.push(file.name || 'that file');
        }

        if (accepted.length) {
            request.files = request.files.concat(accepted);
            // A new page invalidates the last failure — the patient is mid-fix, not mid-error.
            request.uploadError = null;
        }
        request.rejectedFileNames = rejected;
        this.checkDocumentLimits(request);
        this.updateStepCompletionStatus();
        this.cd.detectChanges();
    }

    /** Remove one before sending. The whole point of showing the list. */
    removeDocumentFile(request: InviteDocumentRequest, index: number): void {
        if (index < 0 || index >= request.files.length) return;
        request.files = request.files.slice(0, index).concat(request.files.slice(index + 1));
        this.checkDocumentLimits(request);
        this.cd.detectChanges();
    }

    /**
     * Judge the selection against the caps the SERVER published with this invite.
     *
     * Nothing here is a security control — the server checks again and its check is the one that
     * counts. This exists so a patient on a phone connection is told BEFORE ten photographs are
     * uploaded and then refused.
     */
    checkDocumentLimits(request: InviteDocumentRequest): void {
        request.limitError = null;
        if (!request.files.length) return;

        if (request.maxFiles !== null && request.files.length > request.maxFiles) {
            request.limitError = `You have chosen ${request.files.length} files, and the practice can `
                + `accept ${request.maxFiles}. Please remove ${request.files.length - request.maxFiles}`
                + ' before sending.';
            return;
        }

        const total = this.documentTotalBytes(request);
        if (request.maxTotalBytes !== null && total > request.maxTotalBytes) {
            request.limitError = `Your files come to ${this.bytesLabel(total)} and the practice can `
                + `accept ${this.bytesLabel(request.maxTotalBytes)}. Please remove some, or take the `
                + 'photos again at a smaller size.';
        }
    }

    documentTotalBytes(request: InviteDocumentRequest): number {
        let total = 0;
        for (const file of request.files) total += file.size || 0;
        return total;
    }

    bytesLabel(bytes: number): string {
        if (!bytes) return '0 KB';
        if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    fileSizeLabel(file: File): string {
        const bytes = file ? file.size : 0;
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        return this.bytesLabel(bytes);
    }

    fileIcon(file: File): string {
        return (file && (file.type || '').toLowerCase().indexOf('image/') === 0)
            ? 'image' : 'picture_as_pdf';
    }

    documentPageCountLabel(request: InviteDocumentRequest): string {
        const pages = request.pageCount;
        if (!pages || pages < 1) return '';
        return pages === 1 ? '1 page' : `${pages} pages`;
    }

    canUploadDocument(request: InviteDocumentRequest): boolean {
        return request.files.length > 0 && !request.isUploading && !request.limitError
            && !request.returned;
    }

    trackDocumentFile(index: number, file: File): string {
        return `${index}:${file.name}:${file.size}`;
    }

    trackRejectedName(_index: number, name: string): string {
        return name;
    }

    /* ── Sending them back ──────────────────────────────────────────────────────────────────── */

    /**
     * Return one document — OUT OF BAND, now, not folded into the invite's own submit.
     *
     * See `InviteDocumentRequest`: `submit()` sends `data` + `signature` and nothing else, and the
     * bytes of ten photographs have no business riding the request that completes the invite.
     */
    uploadDocument(request: InviteDocumentRequest): void {
        if (!this.canUploadDocument(request)) return;

        const inviteId = this.inviteRowId;
        const shortToken = this.inviteShortToken;
        if (!inviteId || !shortToken) return;

        request.isUploading = true;
        request.uploadPercent = null;
        request.uploadError = null;
        this.cd.detectChanges();

        this.documentUploadSubs.get(request.id)?.unsubscribe();
        const sub = this.patientInviteService
            .uploadDocumentFiles(inviteId, request.id, shortToken, request.files)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (event) => {
                    if (event.kind === 'progress') {
                        request.uploadPercent = event.percent;
                        this.cd.detectChanges();
                        return;
                    }
                    this.applyDocumentReturned(request, event.result);
                },
                error: (error: unknown) => this.applyDocumentUploadFailure(request, error),
            });
        this.documentUploadSubs.set(request.id, sub);
    }

    /**
     * The pages landed.
     *
     * `alreadyReturned` IS THIS PATH, not the error path: it means an earlier upload landed and
     * this is a retry over a dropped response — the endpoint claims before it works, so the loser
     * of the race replays it rather than filing a second copy of every page. Treating it as a
     * failure would leave a patient who has already finished staring at a blocked step.
     */
    private applyDocumentReturned(request: InviteDocumentRequest, result: InviteDocumentUploadResult): void {
        request.isUploading = false;
        request.uploadPercent = null;
        request.uploadError = null;
        request.returned = true;
        request.returnedMessage = (result && result.message)
            || 'Thank you — your document has been sent to the practice.';
        this.updateStepCompletionStatus();
        this.cd.detectChanges();
    }

    /**
     * An upload that did not land.
     *
     * THE SELECTION IS NEVER TOUCHED HERE. Whatever went wrong, the patient is left looking at the
     * pages they photographed with a way to send them again — asking somebody to re-photograph ten
     * pages because a connection dropped is not a recoverable experience.
     */
    private applyDocumentUploadFailure(request: InviteDocumentRequest, error: unknown): void {
        request.isUploading = false;
        request.uploadPercent = null;

        // Already returned, arriving as a refusal: a second device, or a response we never saw.
        // Their pages are filed, so this is the completion state and not a failure.
        const state = readPublicDocumentState(error);
        const body = (error as { error?: { alreadyReturned?: boolean } } | null)?.error;
        if (state === 'completed' || body?.alreadyReturned === true) {
            request.returned = true;
            request.uploadError = null;
            request.returnedMessage = readPublicDocumentMessage(error)
                || 'You have already sent this document back to the practice.';
            this.updateStepCompletionStatus();
            this.cd.detectChanges();
            return;
        }

        // The refusals in the SERVER'S words — it is the only party that can name the actual
        // numbers ("you attached 14 files, and the limit is 10") or the offending file.
        request.uploadError = readUploadRefusal(error).message;
        this.cd.detectChanges();
    }

    /** The first required document that has not been returned, or null when none is outstanding. */
    private firstOutstandingDocumentStepIndex(): number | null {
        for (let index = 0; index < this.wizardSteps.length; index++) {
            if (this.wizardSteps[index].type !== 'document') continue;
            if (!this.isStepValid(index)) return index;
        }
        return null;
    }

    /**
     * Say what is outstanding on a document step.
     *
     * The mirror of `revealFormStepErrors`, and shorter for the same reason it is longer there: a
     * form has questions to reveal, a document has exactly one outstanding fact.
     */
    private revealDocumentStepErrors(stepIndex: number): void {
        const step = this.wizardSteps[stepIndex];
        if (!step || step.type !== 'document') return;
        this.showToastMessage(this.outstandingDocumentMessage);
    }

    /**
     * The id of the step on screen — what the template branches on from `consent` onward.
     *
     * Everything before `consent` can go on testing `currentStep === n` because nothing is ever
     * spliced in ahead of it. Everything after it cannot: a practice with a two-section form moves
     * `signature` from index 7 to index 9, and a literal index would render the wrong step or none.
     */
    get currentStepId(): string | null {
        const step = this.wizardSteps[this.currentStep];
        return step ? step.id : null;
    }

    /**
     * Whether the button on THIS step finishes the invite rather than advancing — document-only.
     *
     * ── WHY IT IS "THE LAST STEP" AND NOT "THE LAST DOCUMENT STEP" ────────────────────────────
     *
     * A document-only invite whose documents have all been returned loads with ZERO document
     * requests, so its whole step list is `['welcome']`. If Finish lived on the last DOCUMENT step
     * that patient would stand on Welcome with a Get Started button that cannot advance, and the
     * invite would never reach `completedAt` — the practice would see it outstanding forever,
     * which is precisely the failure this mode has to avoid. Anchoring on the last step of the
     * list covers both shapes with one rule.
     *
     * Every other mode ends on `signature`, which has always owned the submit button, so this is
     * false there and the Next button is untouched.
     */
    get showsFinishButton(): boolean {
        return this.documentOnly && this.currentStep === this.totalSteps - 1;
    }

    private updateProgress() {
        this.stepProgress = ((this.currentStep + 1) / this.totalSteps) * 100;
    }

    /**
     * The patient MOVED ON. Re-page the renderers, and un-reveal the errors on the way (§14.7).
     *
     * `resetTouched()` is called on every navigation and NOT on submit, which is the distinction
     * the renderer refused to make for its hosts and was right to refuse: pressing Next on an
     * unfinished section reveals its errors, and without this reset the patient arrives at the
     * next section to a wall of red for questions they have never reached. `submit()` reveals
     * errors deliberately and must keep them showing, so it does the opposite.
     */
    private onStepChanged(): void {
        this.syncFormPaging();
        // The document that just came on screen mints its download URL here, and the one that just
        // left stops being refreshed — `refreshDocumentBlankIfDue` reads the step on screen.
        this.syncDocumentPaging();
        for (const request of this.formRequests) {
            this.rendererFor(request)?.resetTouched();
        }
        this.updateProgress();
    }

    /** Reveal what is outstanding on a form step. The renderer owns which errors those are. */
    private revealFormStepErrors(stepIndex: number): void {
        const step = this.wizardSteps[stepIndex];
        if (!step || step.type !== 'form') return;
        const request = this.formRequestById(step.formRequestId);
        if (!request) return;
        this.rendererFor(request)?.markAllTouched();
        this.showToastMessage(this.incompleteStepMessage);
    }

    nextStep() {
        // A form step that is not finished REVEALS its errors instead of silently refusing to
        // advance (§14.2). Without this the Next button simply does nothing and the patient has no
        // way to find out which question is short.
        if (!this.isStepValid(this.currentStep)) {
            this.revealFormStepErrors(this.currentStep);
            // The same treatment for a required document that has not been returned. Both guard on
            // the step's own type, so exactly one of them ever says anything.
            this.revealDocumentStepErrors(this.currentStep);
        }

        if (this.currentStep < this.totalSteps - 1 && this.isStepValid(this.currentStep)) {
            this.wizardSteps[this.currentStep].completed = true;
            this.currentStep++;
            this.onStepChanged();
            this.autoSaveFormData(); // Auto-save when moving to next step
            // Use setTimeout to ensure the view has updated before scrolling
            setTimeout(() => {
                this.scrollToTop();
            }, 50);
        }
    }

    previousStep() {
        if (this.currentStep > 0) {
            // Mark current step as not completed when going back
            this.wizardSteps[this.currentStep].completed = false;
            this.currentStep--;
            this.onStepChanged();
            this.autoSaveFormData(); // Auto-save when moving to previous step
            // Use setTimeout to ensure the view has updated before scrolling
            setTimeout(() => {
                this.scrollToTop();
            }, 50);
        }
    }

    canNavigateToStep(stepIndex: number): boolean {
        // Always allow navigation to the current step
        if (stepIndex === this.currentStep) {
            return true;
        }

        // Always allow navigation to the welcome step (step 0)
        if (stepIndex === 0) {
            return true;
        }

        // Allow navigation to any completed step
        if (this.wizardSteps[stepIndex]?.completed) {
            return true;
        }

        // Allow navigation to the next step if current step is valid
        if (stepIndex === this.currentStep + 1 && this.isStepValid(this.currentStep)) {
            return true;
        }

        // Allow navigation to previous steps if they are valid
        if (stepIndex < this.currentStep) {
            return this.isStepValid(stepIndex);
        }

        return false;
    }

    goToStep(stepIndex: number) {
        if (stepIndex >= 0 && stepIndex < this.totalSteps && this.canNavigateToStep(stepIndex)) {
            this.currentStep = stepIndex;
            this.onStepChanged();
            this.autoSaveFormData(); // Auto-save when navigating to a step
            // Use setTimeout to ensure the view has updated before scrolling
            setTimeout(() => {
                this.scrollToTop();
            }, 50);
        }
    }

    private scrollToTop() {
        // Scroll the window to top
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Also scroll the scrollable parent if it exists
        setTimeout(() => {
            if (this.scrollableParent) {
                this.scrollableParent.nativeElement.scrollTop = 0;
            }
        }, 100);
    }

    private createConsentForms(consentDetails: any[]) {
        this.consentForms = this.formBuilder.array(
            consentDetails.map(detail => {
                const conditionalConsentArray = this.formBuilder.array(
                    (detail.ConsentForm?.conditionalConsent || []).map((consent: any) =>
                        this.formBuilder.group({
                            consentGiven: [true],
                            name: [consent]
                        })
                    )
                );

                const requiredConsent = detail.ConsentForm?.additionalRequiredConsent || [];
                const confirmSide = detail.ConsentForm?.confirmSide;
                const sideOptions = detail.ConsentForm?.sideOptions;
                const confirmImplant = detail.ConsentForm?.confirmImplant;
                const implantOptions = detail.ConsentForm?.implantOptions;
                const confirmTransfusion = detail.ConsentForm?.confirmTransfusion;
                const transfusionOptions = detail.ConsentForm?.transfusionOptions;

                return this.formBuilder.group({
                    id: [detail.id, Validators.required],
                    inviteToken: [detail.inviteToken, Validators.required],
                    consentLabel: [detail.ConsentForm?.label || '', Validators.required],
                    consentText: [this.buildHtmlFromText(detail.consentText || detail.ConsentForm?.text) || '', Validators.required],
                    consentGiven: [false, Validators.required],
                    form: [detail.ConsentForm || null],
                    conditionalConsent: conditionalConsentArray,
                    additionalRequiredConsent: [requiredConsent],
                    selectedRequiredConsent: [requiredConsent.length ? requiredConsent[0] : ''],
                    selectedProcedureSide: [''],
                    confirmSide: [confirmSide],
                    sideOptions: [sideOptions],
                    selectedImplant: [''],
                    confirmImplant: [confirmImplant],
                    implantOptions: [implantOptions],
                    selectedTransfusion: [''],
                    confirmTransfusion: [confirmTransfusion],
                    transfusionOptions: [transfusionOptions],
                });
            })
        );
    }

    get consentFormGroups(): FormGroup[] {
        return (this.consentForms.controls as FormGroup[]);
    }

    getConditionalConsentArray(form: FormGroup): FormArray {
        return form.get('conditionalConsent') as FormArray;
    }

    private buildHtmlFromText(text: string): string {
        if (!text) return text;
        return text.replace(/\n/g, '<br>');
    }

    initForms() {
        this.personalInformationForm = this.formBuilder.group({
            id: [''],
            consentOnly: [false],
            patientId: [''],
            groupId: [''],
            title: [''],
            initials: [''],
            name: ['', Validators.required],
            surname: ['', Validators.required],
            dob: ['', Validators.required],
            gender: ['', Validators.required],
            country: [''],
            idType: [''],
            idNumber: ['', [Validators.required, SaIdValidator.isValidSaId()]],
            passportNumber: [''],
            passportCountry: [''],
        });

        this.contactInformationForm = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            phoneNumber: [''],
            mobileNumber: ['', Validators.required],
            address: ['', Validators.required],
            addressCode: [''],
            religion: [''],
            language: [''],
            occupation: [''],
            employer: [''],
            workPhone: [''],
            workAddress: [''],
            workAddressCode: [''],
            maritalStatus: [''],
        });

        this.emergencyContactForm = this.formBuilder.group({
            hasEmergencyContact: [null],
            emergencyContact: [''],
            emergencyContactRelationship: [''],
            emergencyContactMobile: [''],
            emergencyContactPhone: [''],
            emergencyContactWork: [''],
            emergencyContactEmail: ['', Validators.email],
            emergencyContactAddress: [''],
            emergencyContactAddressCode: [''],
            emergencyContact2: [''],
            emergencyContactRelationship2: [''],
            emergencyContactMobile2: [''],
            emergencyContactPhone2: [''],
            emergencyContactWork2: [''],
            emergencyContactEmail2: ['', Validators.email],
            emergencyContactAddress2: [''],
            emergencyContactAddressCode2: [''],
        }, { validators: EmergencyContactValidator.validateEmergencyContacts() });

        this.medicalInsuranceForm = this.formBuilder.group({
            height: [''],
            weight: [''],
            reasonForVisiting: ['', [this.wordCountValidator(20)]],
        });

        this.personLiableForm = this.formBuilder.group({
            liableTitle: [''],
            liableInitials: [''],
            liableName: [''],
            liableSurname: [''],
            liableIdType: ['id'],
            liableIdNumber: ['', SaIdValidator.isValidSaId()],
            liablePassportNumber: [''],
            liablePassportCountry: [''],
            liableEmail: ['', Validators.email],
            liableMobileNumber: [''],
            liablePhoneNumber: [''],
            liableAddress: [''],
            liableAddressCode: [''],
            liableOccupation: [''],
            liableEmployer: [''],
            liableWorkPhone: [''],
            liableWorkAddress: [''],
            liableWorkAddressCode: [''],
            // Medical Aid fields
            hasMedicalAid: [null],
            medicalAid: [''],
            // The scheme / plan / option picked from the list, or null for "can't find my medical aid". Left undefined
            // until the patient picks, so a submission without a pick changes no codes.
            schemeLink: [undefined],
            medicalAidNumber: [''],
            medicalAidPlan: [''],
            medicalMainMember: [''],
            medicalDependantCode: [''],
            medicalDateJoined: [''],
            hasGapCover: [null],
            gapCover: [''],
            gapCoverNumber: [''],
        });

        this.signatureForm = this.formBuilder.group({
            signature: ['', Validators.required],
        });

        this.allergyForm = this.formBuilder.group({
            allergies: [[]],
            noAllergies: [null],
        });

        this.medicalHistoryForm = this.formBuilder.group({
            medicalHistory: [[]],
            noMedicalHistory: [null],
        });

        this.medicationHistoryForm = this.formBuilder.group({
            medicationHistory: [[]],
            noMedicationHistory: [null],
        });

        this.medicalTeamForm = this.formBuilder.group({
            medicalTeam: [[]]
        });

        this.referralSourceForm = this.formBuilder.group({
            whereDidYouHearAboutUs: ['']
        });

        this.consentForms = this.formBuilder.array([]);


        FormUtils.showAllFormErrorStates(this.personalInformationForm);
        FormUtils.showAllFormErrorStates(this.contactInformationForm);
        FormUtils.showAllFormErrorStates(this.medicalInsuranceForm);
        FormUtils.showAllFormErrorStates(this.personLiableForm);
        FormUtils.showAllFormErrorStates(this.signatureForm);
        FormUtils.showAllFormErrorStates(this.allergyForm);
        FormUtils.showAllFormErrorStates(this.medicalHistoryForm);
    }

    setupChangeListeners() {
        this.personalInformationForm.get('idNumber')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                this.onIdNumberChange(value);
                this.updateStepCompletionStatus();
            });

        // Medical aid toggle listener
        this.personLiableForm.get('hasMedicalAid')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((hasMedicalAid) => {
                this.toggleMedicalAidFields(hasMedicalAid);
                this.updateStepCompletionStatus();
            });

        // Auto-calculate initials for personal information
        this.personalInformationForm.get('name')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.calculateInitials();
                this.updateInviteName();
                this.updateStepCompletionStatus();
            });

        this.personalInformationForm.get('surname')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.calculateInitials();
                this.updateInviteName();
                this.updateStepCompletionStatus();
            });

        // Auto-calculate initials for account holder
        this.personLiableForm.get('liableName')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.calculateLiableInitials();
                this.updateStepCompletionStatus();
            });

        this.personLiableForm.get('liableSurname')!.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.calculateLiableInitials();
                this.updateStepCompletionStatus();
            });

        // Listen to form changes to update step completion status
        this.personalInformationForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateStepCompletionStatus();
                this.autoSaveFormData();
            });

        this.contactInformationForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateStepCompletionStatus();
                this.autoSaveFormData();
            });

        this.personLiableForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateStepCompletionStatus();
                this.autoSaveFormData();
            });

        this.emergencyContactForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateStepCompletionStatus();
                this.autoSaveFormData();
            });

        this.allergyForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateStepCompletionStatus();
                this.autoSaveFormData();
            });

        this.medicalHistoryForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateStepCompletionStatus();
                this.autoSaveFormData();
            });

        this.medicationHistoryForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateStepCompletionStatus();
                this.autoSaveFormData();
            });

        this.consentForms.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateStepCompletionStatus();
                this.autoSaveFormData();
            });

        this.signatureForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateStepCompletionStatus();
                // Note: Signature form changes are not auto-saved
            });
    }

    private updateStepCompletionStatus() {
        // Update completion status for all steps based on current form state
        for (let i = 0; i < this.wizardSteps.length; i++) {
            if (i < this.currentStep) {
                // Steps before current step should remain completed if they were valid
                this.wizardSteps[i].completed = this.wizardSteps[i].completed && this.isStepValid(i);
            } else if (i === this.currentStep) {
                // Current step completion status depends on its validity
                this.wizardSteps[i].completed = this.isStepValid(i);
            } else {
                // Future steps should not be marked as completed
                this.wizardSteps[i].completed = false;
            }
        }
    }

    /** The scheme list for the medical aid picker, read with the invite token. Without it the picker offers typing only. */
    private loadMedicalAidSchemes() {
        this.patientInviteService.getSchemeReference(this.inviteToken!)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => this.medicalAidSchemes = result?.data?.schemes || [],
                error: () => this.medicalAidSchemes = [],
            });
    }

    /** What the picker starts on: a pick or typed text restored from the saved draft. Read once per showing. */
    get medicalAidInitial(): MedicalAidPickerInitial | null {
        if (this._medicalAidInitial === undefined) {
            const v = this.personLiableForm.value;
            this._medicalAidInitial = v.schemeLink
                ? { ...v.schemeLink }
                : v.medicalAid && v.medicalAid !== 'Private Patient' ? { medicalAid: v.medicalAid, medicalAidPlan: v.medicalAidPlan } : null;
        }
        return this._medicalAidInitial ?? null;
    }

    onMedicalAidPick(pick: MedicalAidPick | null) {
        if (!pick) {
            this.personLiableForm.patchValue({ medicalAid: '', medicalAidPlan: '', schemeLink: undefined });
        } else {
            const fields = medicalAidFields(pick);
            const codes = 'scheme' in pick ? { schemeCode: pick.scheme.code, planCode: pick.option.planCode, optionCode: pick.option.optionCode } : null;
            this.personLiableForm.patchValue({ medicalAid: fields.medicalAid, medicalAidPlan: fields.medicalAidPlan, schemeLink: codes });
        }
        this.updateStepCompletionStatus();
    }

    selectMedicalAidOption(hasMedicalAid: boolean) {
        this.personLiableForm.patchValue({ hasMedicalAid });
        if (hasMedicalAid) {
            this.personLiableForm.patchValue({ medicalAid: '', medicalAidPlan: '', schemeLink: undefined });
            this._medicalAidInitial = null;
        }
        this.toggleMedicalAidFields(hasMedicalAid);
        this.updateStepCompletionStatus();
    }

    selectAllergiesOption(hasAllergies: boolean) {
        this.allergyForm.patchValue({ allergies: [], noAllergies: !hasAllergies });
        this.updateStepCompletionStatus();
    }

    selectMedicalHistoryOption(hasMedicalHistory: boolean) {
        this.medicalHistoryForm.patchValue({ medicalHistory: [], noMedicalHistory: !hasMedicalHistory });
        this.updateStepCompletionStatus();
    }

    selectMedicationsOption(hasMedications: boolean) {
        this.medicationHistoryForm.patchValue({ medicationHistory: [], noMedicationHistory: !hasMedications });
        this.updateStepCompletionStatus();
    }

    selectGapCoverOption(hasGapCover: boolean) {
        this.personLiableForm.patchValue({ hasGapCover });
        this.toggleGapCoverFields(hasGapCover);
        this.updateStepCompletionStatus();
    }

    toggleMedicalAidFields(hasMedicalAid: boolean) {
        const controls = this.personLiableForm.controls;

        if (hasMedicalAid) {
            controls['medicalAid'].setValidators([Validators.required]);
            controls['medicalDependantCode'].setValidators([Validators.required]);
            controls['medicalAidNumber'].setValidators([Validators.required]);
            controls['medicalAidPlan'].setValidators([Validators.required]);
            controls['medicalMainMember'].setValidators([Validators.required]);
        } else {
            controls['medicalAid'].clearValidators();
            controls['medicalDependantCode'].clearValidators();
            controls['medicalAidNumber'].clearValidators();
            controls['medicalAidPlan'].clearValidators();
            controls['medicalMainMember'].clearValidators();
            this.personLiableForm.patchValue({
                medicalAid: 'Private Patient',
                schemeLink: null,
                medicalAidNumber: '',
                medicalAidPlan: '',
                medicalDependantCode: '',
                medicalMainMember: ''
            });
        }

        this.personLiableForm.updateValueAndValidity();
        FormUtils.showAllFormErrorStates(this.personLiableForm);
    }

    toggleGapCoverFields(hasGapCover: boolean) {
        const controls = this.personLiableForm.controls;

        if (hasGapCover) {
            controls['gapCover'].setValidators([Validators.required]);
            controls['gapCoverNumber'].setValidators([Validators.required]);
        } else {
            controls['gapCover'].clearValidators();
            controls['gapCoverNumber'].clearValidators();
            this.personLiableForm.patchValue({
                gapCover: '',
                gapCoverNumber: ''
            });
        }

        this.personLiableForm.updateValueAndValidity();
        FormUtils.showAllFormErrorStates(this.personLiableForm);
    }

    onIDTypeChange(event: MatSelectChange, isLiableId?: Boolean) {
        if (isLiableId) {
            switch (event.value) {
                case 'id':
                    this.personLiableForm.patchValue({ liablePassportNumber: '', liablePassportCountry: '' });
                    break;
                case 'passport':
                    this.personLiableForm.patchValue({ liableIdNumber: '', liablePassportCountry: '' });
                    break;
            }
        }
    }

    private onIdNumberChange(value: string) {
        const cleanedId = value ? value.replace(/\D/g, '') : '';
        const idControl = this.personalInformationForm.get('idNumber');

        if (cleanedId.length === 13 && idControl!.valid) {
            const dob = SaIdValidator.extractDateOfBirth(cleanedId);
            const genderDigit = cleanedId.charAt(6);

            if (dob) {
                this.personalInformationForm.get('dob')!.setValue(dob);
                const gender = parseInt(genderDigit, 10) >= 5 ? 'Male' : 'Female';
                this.personalInformationForm.get('gender')!.setValue(gender);
            } else {
                this.personalInformationForm.get('dob')!.setValue('');
                this.personalInformationForm.get('gender')!.setValue('');
            }
        } else {
            this.personalInformationForm.get('dob')!.setValue('');
            this.personalInformationForm.get('gender')!.setValue('');
        }
    }

    configureFormValidation(controls: { [key: string]: AbstractControl; }) {
        if (this.idTypeSelection === 'sa-id') {
            controls['passportNumber'].clearValidators();
            controls['idNumber'].setValidators([Validators.required, SaIdValidator.isValidSaId()]);
            controls['passportCountry'].clearValidators();
        } else if (this.idTypeSelection === 'foreign-passport') {
            controls['idNumber'].clearValidators();
            controls['passportNumber'].setValidators([Validators.required]);
            controls['passportCountry'].setValidators([Validators.required]);
        }

        controls['idNumber'].updateValueAndValidity();
        controls['passportNumber'].updateValueAndValidity();
        controls['passportCountry'].updateValueAndValidity();
        this.personalInformationForm.updateValueAndValidity();
        FormUtils.showAllFormErrorStates(this.personalInformationForm);
    }

    clearFormValidators() {
        this.personalInformationForm.clearValidators();
        this.personalInformationForm.updateValueAndValidity();
        this.contactInformationForm.clearValidators();
        this.contactInformationForm.updateValueAndValidity();
        this.medicalInsuranceForm.clearValidators();
        this.medicalInsuranceForm.updateValueAndValidity();
        /* FormUtils.showAllFormErrorStates(this.medicalInsuranceForm);
        FormUtils.showAllFormErrorStates(this.personLiableForm);
        FormUtils.showAllFormErrorStates(this.signatureForm);
        FormUtils.showAllFormErrorStates(this.allergyForm);
        FormUtils.showAllFormErrorStates(this.medicalHistoryForm);
        FormUtils.showAllFormErrorStates(this.medicationHistoryForm);
        FormUtils.showAllFormErrorStates(this.medicalTeamForm); */
    }

    getSignatureData(base64Image: any) {
        this.signatureForm.patchValue({
            signature: base64Image
        });
    }

    isSignaturePadValid(): boolean {
        // Safe check for signature pad component existence and validity
        return this.signaturePadComponent ? this.signaturePadComponent.isValid() : false;
    }

    onMedicalTeamChanged(team: any[]) {
        this.medicalTeamForm.patchValue({ medicalTeam: team });
    }

    onAllergiesChanged(allergies: any[]) {
        this.allergyForm.patchValue({ allergies: allergies });
        this.updateStepCompletionStatus();
    }

    onMedicalHistoryChanged(history: any[]) {
        this.medicalHistoryForm.patchValue({ medicalHistory: history });
        this.updateStepCompletionStatus();
    }

    onMedicationChanged(medication: any[]) {
        this.medicationHistoryForm.patchValue({ medicationHistory: medication });
        this.updateStepCompletionStatus();
    }

    onNoAllergiesChanged(noAllergies: boolean) {
        this.allergyForm.patchValue({ noAllergies: noAllergies });
        this.updateStepCompletionStatus();
    }

    onNoMedicalHistoryChanged(noHistory: boolean) {
        this.medicalHistoryForm.patchValue({ noMedicalHistory: noHistory });
        this.updateStepCompletionStatus();
    }

    onNoMedicationChanged(noMedication: boolean) {
        this.medicationHistoryForm.patchValue({ noMedicationHistory: noMedication });
        this.updateStepCompletionStatus();
    }

    private dataURLToBlob(dataURL: string) {
        const byteString = atob(dataURL.split(",")[1]);
        const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];

        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new Blob([ia], { type: mimeString });
    }

    private getAllDetails() {
        return {
            ...this.personalInformationForm?.value,
            ...this.contactInformationForm?.value,
            ...this.emergencyContactForm?.value,
            ...this.medicalInsuranceForm?.value,
            ...this.personLiableForm?.value,
            ...this.signatureForm?.value,
            ...this.allergyForm?.value,
            ...this.medicalHistoryForm?.value,
            ...this.medicationHistoryForm?.value,
            ...this.medicalTeamForm?.value,
        }
    }

    calculateInitials() {
        const firstName = this.personalInformationForm.get('name')?.value || '';
        const lastName = this.personalInformationForm.get('surname')?.value || '';

        let initials = '';
        if (firstName.trim()) {
            initials += firstName.trim().charAt(0).toUpperCase();
        }
        if (lastName.trim()) {
            initials += lastName.trim().charAt(0).toUpperCase();
        }

        this.personalInformationForm.patchValue({ initials }, { emitEvent: false });
    }

    calculateLiableInitials() {
        const firstName = this.personLiableForm.get('liableName')?.value || '';
        const lastName = this.personLiableForm.get('liableSurname')?.value || '';

        let initials = '';
        if (firstName.trim()) {
            initials += firstName.trim().charAt(0).toUpperCase();
        }
        if (lastName.trim()) {
            initials += lastName.trim().charAt(0).toUpperCase();
        }

        this.personLiableForm.patchValue({ liableInitials: initials }, { emitEvent: false });
    }

    private updateInviteName() {
        const firstName = this.personalInformationForm.get('name')?.value || '';
        const lastName = this.personalInformationForm.get('surname')?.value || '';

        // Update inviteName with the current name values
        if (firstName.trim() || lastName.trim()) {
            this.inviteName = `${firstName.trim()} ${lastName.trim()}`.trim();
        } else {
            // Fallback to original invite name if no name is entered
            this.inviteName = this.inviteDetails?.Patient?.name || '';
        }
    }

    selectIdTypeOption(selection: string) {
        this.idTypeSelection = selection;

        if (selection === 'sa-id') {
            // Set country to South Africa, hide country field, set ID type to SA ID
            this.personalInformationForm.patchValue({
                country: 'South Africa',
                idType: 'id'
            });
            this.configureFormValidation(this.personalInformationForm.controls);
        } else if (selection === 'foreign-passport') {
            // Show country field, set ID type to passport
            this.personalInformationForm.patchValue({
                idType: 'passport'
            });
            this.configureFormValidation(this.personalInformationForm.controls);
        }
        this.updateStepCompletionStatus();
    }

    selectAccountHolderOption(sameAsPatient: boolean) {
        this.accountHolderSame = sameAsPatient;

        if (sameAsPatient) {
            // Copy patient details to account holder
            const values = this.getAllDetails();
            const data = {
                liableTitle: values.title,
                liableName: values.name,
                liableInitials: values.initials,
                liableSurname: values.surname,
                liableIdType: values.idType,
                liableIdNumber: values.idNumber,
                liablePassportNumber: values.passportNumber,
                liablePassportCountry: values.passportCountry,
                liableEmail: values.email,
                liableMobileNumber: values.mobileNumber,
                liablePhoneNumber: values.phoneNumber,
                liableAddress: values.address,
                liableAddressCode: values.addressCode,
                liableOccupation: values.occupation,
                liableEmployer: values.employer,
                liableWorkPhone: values.workPhone,
                liableWorkAddress: values.workAddress,
                liableWorkAddressCode: values.workAddressCode,
            };
            this.personLiableForm!.patchValue(data);

            // Clear validators for liable person fields since they're copied
            this.clearLiablePersonValidators();

            // Recalculate initials after copying
            setTimeout(() => {
                this.calculateLiableInitials();
            }, 100);
        } else {
            // Clear account holder details
            const data = {
                liableTitle: '',
                liableName: '',
                liableInitials: '',
                liableSurname: '',
                liableIdType: '',
                liableIdNumber: '',
                liablePassportNumber: '',
                liablePassportCountry: '',
                liableEmail: '',
                liableMobileNumber: '',
                liablePhoneNumber: '',
                liableAddress: '',
                liableAddressCode: '',
                liableOccupation: '',
                liableEmployer: '',
                liableWorkPhone: '',
                liableWorkAddress: '',
                liableWorkAddressCode: '',
            };
            this.personLiableForm!.patchValue(data);

            // Set validators for liable person fields since they need to be filled
            this.setLiablePersonValidators();
        }
        this.updateStepCompletionStatus();
    }

    private clearLiablePersonValidators() {
        const controls = this.personLiableForm.controls;
        const liableFields = ['liableName', 'liableSurname', 'liableEmail', 'liableIdNumber', 'liablePassportNumber', 'liablePassportCountry'];

        liableFields.forEach(field => {
            if (controls[field]) {
                controls[field].clearValidators();
                controls[field].updateValueAndValidity();
            }
        });
    }

    private setLiablePersonValidators() {
        const controls = this.personLiableForm.controls;

        // Set required validators for basic fields
        controls['liableName'].setValidators([Validators.required]);
        controls['liableSurname'].setValidators([Validators.required]);
        controls['liableEmail'].setValidators([Validators.required, Validators.email]);

        // ID validation will be handled by selectLiableIdTypeOption
        this.personLiableForm.updateValueAndValidity();
        FormUtils.showAllFormErrorStates(this.personLiableForm);
    }

    copyPatientDetails(event: any) {
        const values = this.getAllDetails();
        let data = {
            liableTitle: '',
            liableName: '',
            liableInitials: '',
            liableSurname: '',
            liableIdType: '',
            liableIdNumber: '',
            liablePassportNumber: '',
            liablePassportCountry: '',
            liableEmail: '',
            liableMobileNumber: '',
            liablePhoneNumber: '',
            liableAddress: '',
            liableAddressCode: '',
            liableOccupation: '',
            liableEmployer: '',
            liableWorkPhone: '',
            liableWorkAddress: '',
            liableWorkAddressCode: '',
        }

        if (!event.checked) {
            this.personLiableForm!.patchValue(data);
            return;
        }

        data = {
            liableTitle: values.title,
            liableName: values.name,
            liableInitials: values.initials,
            liableSurname: values.surname,
            liableIdType: values.idType,
            liableIdNumber: values.idNumber,
            liablePassportNumber: values.passportNumber,
            liablePassportCountry: values.passportCountry,
            liableEmail: values.email,
            liableMobileNumber: values.mobileNumber,
            liablePhoneNumber: values.phoneNumber,
            liableAddress: values.address,
            liableAddressCode: values.addressCode,
            liableOccupation: values.occupation,
            liableEmployer: values.employer,
            liableWorkPhone: values.workPhone,
            liableWorkAddress: values.workAddress,
            liableWorkAddressCode: values.workAddressCode,
        }

        this.personLiableForm!.patchValue(data);

        // Recalculate initials after copying
        setTimeout(() => {
            this.calculateLiableInitials();
        }, 100);
    }

    formIsValid(): boolean {
        let isValid = true;

        if (!this.consentOnly) {
            if (!this.personalInformationForm.valid) {
                isValid = false;
            }

            if (!this.contactInformationForm.valid) {
                isValid = false;
            }
        }

        if (this.consentForms.value.some((consent: any) => !consent.consentGiven)) {
            isValid = false;
        }

        if (!this.signatureForm.valid) {
            isValid = false;
        }

        // Check signature pad validity if signatory fields are required
        if (!this.isSignaturePadValid()) {
            isValid = false;
        }

        return isValid;
    }

    /**
     * Validity of ONE step, dispatched on the step's ID rather than its index.
     *
     * The indices stopped being stable the moment practice forms could splice steps in between
     * `consent` and `signature` (§6): a numeric `case 7` would silently start meaning "the first
     * form section" on any invite carrying a form, and the signature step — the one the submit
     * button depends on — would fall through to `default: true`.
     *
     * The `consentOnly` short-circuits are carried over EXACTLY as the index version had them,
     * including on `consent` and `signature`, where a consent-only invite has always returned
     * `true` regardless. That is pre-existing behaviour on the highest-traffic path in the client
     * and this change is not the place to alter it; `submit()` re-checks both before sending.
     */
    isStepValid(step: number): boolean {
        const wizardStep = this.wizardSteps[step];
        if (!wizardStep) return true;

        // A spliced form step defers ENTIRELY to the renderer, and to `isSectionValid` rather than
        // to `valid` — so step 1 is never blocked by a blank question three steps further on
        // (§14.2). A renderer that has not been created yet reports valid: there is nothing on
        // screen for the patient to have got wrong.
        if (wizardStep.type === 'form') {
            const request = this.formRequestById(wizardStep.formRequestId);
            if (!request) return true;
            const renderer = this.rendererFor(request);
            return renderer ? renderer.isSectionValid(wizardStep.sectionId) : true;
        }

        // A spliced DOCUMENT step is valid unless the practice marked it required for this invite
        // and nothing has come back yet (§19). The block is PER DOCUMENT — `requiredInInvite` on
        // the request row — and deliberately not a `group.enforcedInviteSections` key: that setting
        // is a per-group authority over the wizard's own sections, and forms do not join it either.
        if (wizardStep.type === 'document') {
            const request = this.documentRequestById(wizardStep.documentRequestId);
            if (!request) return true;
            return !request.requiredInInvite || request.returned;
        }

        switch (wizardStep.id) {
            case 'welcome': // Welcome step - always valid
                return true;

            case 'personal': // Personal Information
                if (this.consentOnly) return true;
                return this.isPersonalInfoValid();

            case 'contact': // Contact Information
                if (this.consentOnly) return true;
                return this.contactInformationForm.valid;

            case 'account': // Medical Aid & Account Details
                if (this.consentOnly) return true;
                return this.isMedicalAidAccountValid();

            case 'medical': // Medical Details
                if (this.consentOnly) return true;
                return this.isMedicalDetailsValid();

            case 'emergency': // Emergency Contact
                if (this.consentOnly) return true;
                return this.isEmergencyContactValid();

            // No `consentOnly` short-circuit, unlike the steps above. The index version had one,
            // but the consent step's Next button called `isStepValid(6)` in BOTH modes and
            // therefore reached `isConsentValid()` on a consent-only invite too — the guard and
            // the button disagreed, and the button is what the patient saw. This keeps the
            // button's behaviour and makes `nextStep()` agree with it.
            case 'consent': // Consent Forms
                return this.isConsentValid();

            case 'signature': // Signature
                if (this.consentOnly) return true;
                return this.signaturePadComponent?.isValid() || false;

            default:
                return true;
        }
    }

    private isPersonalInfoValid(): boolean {
        if (!this.personalInformationForm.valid) return false;

        // Check if ID type is selected
        if (!this.idTypeSelection) return false;

        // Check if required fields based on ID type are filled
        if (this.idTypeSelection === 'sa-id') {
            return this.personalInformationForm.get('idNumber')?.valid || false;
        } else if (this.idTypeSelection === 'foreign-passport') {
            return (this.personalInformationForm.get('passportNumber')?.valid || false) &&
                !!this.personalInformationForm.get('passportCountry')?.value;
        }

        return false;
    }

    private isMedicalAidAccountValid(): boolean {
        // Check if medical aid question is answered
        if (this.personLiableForm.get('hasMedicalAid')?.value === null) return false;

        // Check if account holder question is answered
        if (this.accountHolderSame === undefined || this.accountHolderSame === null) return false;

        // If medical aid is selected, check required fields
        if (this.personLiableForm.get('hasMedicalAid')?.value === true) {
            const requiredFields = ['medicalAid', 'medicalAidNumber', 'medicalDependantCode', 'medicalAidPlan'];
            for (const field of requiredFields) {
                if (!this.personLiableForm.get(field)?.value) return false;
            }

            if (this.personLiableForm.get('hasGapCover')?.value === null) return false;

            // If gap cover is selected, check required fields
            if (this.personLiableForm.get('hasGapCover')?.value === true) {
                const gapFields = ['gapCover', 'gapCoverNumber'];
                for (const field of gapFields) {
                    if (!this.personLiableForm.get(field)?.value) return false;
                }
            }
        }

        // If different person is responsible, check required fields
        if (this.accountHolderSame === false) {
            const requiredFields = ['liableName', 'liableSurname', 'liableEmail'];
            for (const field of requiredFields) {
                if (!this.personLiableForm.get(field)?.value) return false;
            }

            // Check ID type selection and required fields
            if (!this.liableIdTypeSelection) return false;

            if (this.liableIdTypeSelection === 'sa-id') {
                if (!this.personLiableForm.get('liableIdNumber')?.valid) return false;
            } else if (this.liableIdTypeSelection === 'foreign-passport') {
                if (!this.personLiableForm.get('liablePassportNumber')?.value ||
                    !this.personLiableForm.get('liablePassportCountry')?.value) return false;
            }
        }

        return true;
    }

    private isMedicalDetailsValid(): boolean {
        // Check if allergies question has been answered
        const allergiesAnswered = this.allergyForm.get('noAllergies')?.value !== null;

        // If user said they have allergies, check that at least one allergy is entered
        const allergiesValid = allergiesAnswered &&
            (this.allergyForm.get('noAllergies')?.value === true ||
                (this.allergyForm.get('noAllergies')?.value === false &&
                    this.allergyForm.get('allergies')?.value?.length > 0));

        // Check if medical history question has been answered
        const medicalHistoryAnswered = this.medicalHistoryForm.get('noMedicalHistory')?.value !== null;

        // If user said they have medical history, check that at least one item is entered
        const medicalHistoryValid = medicalHistoryAnswered &&
            (this.medicalHistoryForm.get('noMedicalHistory')?.value === true ||
                (this.medicalHistoryForm.get('noMedicalHistory')?.value === false &&
                    this.medicalHistoryForm.get('medicalHistory')?.value?.length > 0));

        // Check if medications question has been answered
        const medicationsAnswered = this.medicationHistoryForm.get('noMedicationHistory')?.value !== null;

        // If user said they have medications, check that at least one medication is entered
        const medicationsValid = medicationsAnswered &&
            (this.medicationHistoryForm.get('noMedicationHistory')?.value === true ||
                (this.medicationHistoryForm.get('noMedicationHistory')?.value === false &&
                    this.medicationHistoryForm.get('medicationHistory')?.value?.length > 0));

        // Check if reasonForVisiting field is valid (optional field - if it has a value, it must not exceed 20 word limit)
        const reasonForVisitingControl = this.medicalInsuranceForm.get('reasonForVisiting');
        // Field is optional: empty values are valid, but if a value is provided, it must pass validation (word count <= 20)
        const reasonForVisitingValid = !reasonForVisitingControl?.value || reasonForVisitingControl?.valid;

        return allergiesValid && medicalHistoryValid && medicationsValid && reasonForVisitingValid;
    }

    private getMedicalDetailsValidationMessage(): string {
        const messages: string[] = [];

        // Check allergies
        if (this.allergyForm.get('noAllergies')?.value === null) {
            messages.push("Please answer the allergies question");
        } else if (this.allergyForm.get('noAllergies')?.value === false &&
            this.allergyForm.get('allergies')?.value?.length === 0) {
            messages.push("Please add at least one allergy or medical alert");
        }

        // Check medical history
        if (this.medicalHistoryForm.get('noMedicalHistory')?.value === null) {
            messages.push("Please answer the medical history question");
        } else if (this.medicalHistoryForm.get('noMedicalHistory')?.value === false &&
            this.medicalHistoryForm.get('medicalHistory')?.value?.length === 0) {
            messages.push("Please add at least one medical history item or operation");
        }

        // Check medications
        if (this.medicationHistoryForm.get('noMedicationHistory')?.value === null) {
            messages.push("Please answer the medications question");
        } else if (this.medicationHistoryForm.get('noMedicationHistory')?.value === false &&
            this.medicationHistoryForm.get('medicationHistory')?.value?.length === 0) {
            messages.push("Please add at least one medication, supplement, or treatment");
        }

        // Check reasonForVisiting word count
        const reasonForVisitingControl = this.medicalInsuranceForm.get('reasonForVisiting');
        if (reasonForVisitingControl?.value && reasonForVisitingControl?.hasError('wordCountExceeded')) {
            const error = reasonForVisitingControl.errors?.['wordCountExceeded'];
            messages.push(error?.message || "Please limit your reason for visiting to 20 words or less");
        }

        return messages.join(". ");
    }

    private isEmergencyContactValid(): boolean {
        // Check if emergency contact question has been answered
        const hasEmergencyContact = this.emergencyContactForm.get('hasEmergencyContact')?.value;
        const isEnforced = this.group?.enforcedInviteSections?.nextOfKin === true;
        
        // If enforced, hasEmergencyContact should be true, just check form validity
        if (isEnforced) {
            return this.emergencyContactForm.valid;
        }
        
        if (hasEmergencyContact === null) {
            return false; // User hasn't made a choice yet
        }
        
        if (hasEmergencyContact === false) {
            return true; // User chose not to provide emergency contacts
        }
        
        // User chose to provide emergency contacts, check if form is valid
        return this.emergencyContactForm.valid;
    }

    private getEmergencyContactValidationMessage(): string {
        const hasEmergencyContact = this.emergencyContactForm.get('hasEmergencyContact')?.value;
        
        if (hasEmergencyContact === null) {
            return "Please select whether you want to provide emergency contact information.";
        }
        
        if (hasEmergencyContact === true) {
            const errors = this.emergencyContactForm.errors;
            if (errors?.['emergencyContactRequired']) {
                return errors['emergencyContactRequired'].message;
            }
            return "Please ensure at least one emergency contact has a name and either a mobile number or email address.";
        }
        
        return "";
    }

    private isConsentValid(): boolean {
        return !this.consentForms.value.some((consent: any) => !consent.consentGiven);
    }

    /** The first spliced form step that is not finished, or null when every one of them is. */
    private firstInvalidFormStepIndex(): number | null {
        for (let index = 0; index < this.wizardSteps.length; index++) {
            if (this.wizardSteps[index].type !== 'form') continue;
            if (!this.isStepValid(index)) return index;
        }
        return null;
    }

    private wordCountValidator(maxWords: number) {
        return (control: AbstractControl): {[key: string]: any} | null => {
            if (!control.value) {
                return null; // Don't validate empty values
            }
            
            const words = control.value.trim().split(/\s+/).filter((word: string) => word.length > 0);
            
            if (words.length > maxWords) {
                return { 'wordCountExceeded': { 
                    message: `Please limit your response to ${maxWords} words or less. You have used ${words.length} words.`,
                    actualWords: words.length,
                    maxWords: maxWords
                }};
            }
            
            return null;
        };
    }

    getWordCount(text: string): number {
        if (!text) return 0;
        return text.trim().split(/\s+/).filter((word: string) => word.length > 0).length;
    }

    /**
     * FINISH A DOCUMENT-ONLY INVITE — §21.2, and the decision this mode turns on.
     *
     * ── IT SUBMITS. IT DOES NOT "COMPLETE WHEN THE DOCUMENTS ARE RETURNED" ────────────────────
     *
     * The pages themselves are already filed: a return is uploaded out of band, immediately, on
     * its own step (§20.2), and nothing document-shaped rides this request. So there is a fair
     * question about whether this invite needs to submit at all — and it does, for one reason:
     * `completedAt` / `active: false` is written by exactly ONE thing in this system, the
     * conditional claim in `updateInvite`. Nothing else sets it.
     *
     * Completing the invite as a side effect of the last return instead would mean:
     *   - a second completion route, so the `alreadyCompleted` replay covers one of the two;
     *   - no answer at all for an invite whose documents are every one of them optional and the
     *     patient returns none — it would stay open forever, which is the exact failure mode the
     *     practice sees as "still outstanding";
     *   - and a write to `PatientInvite` from inside a route whose entire authorisation is
     *     `id = :requestId AND patientInviteId = invite.id`.
     *
     * So the invite ends the same way every other invite ends: the same PUT, the same claim, the
     * same replay, the same `patientInviteCompleted` notification.
     *
     * ── WHAT IT SENDS, AND WHAT IT DELIBERATELY DOES NOT ──────────────────────────────────────
     *
     * `data` only. NO `signature` part — there is no signature step and nothing to sign — and no
     * patient details of any kind, because a document-only invite collected none: the patient was
     * never shown personal, contact, account, medical or emergency. The payload names the mode and
     * carries two empty collections, so the server's `consents` and `forms` branches are not
     * entered rather than being entered with rubbish.
     *
     * `id` is deliberately absent from the body: the invite is addressed by the URL, and the only
     * thing a body `id` has ever done on this path is ride into `patient.update({...updateData})`.
     */
    private submitDocumentOnly(): void {
        // The same sweep the full path runs, and for the same reason: a required document that has
        // not come back takes the patient BACK TO ITS STEP, because the thing they have to do is
        // on that step and nowhere else.
        const outstandingDocumentStep = this.firstOutstandingDocumentStepIndex();
        if (outstandingDocumentStep !== null) {
            this.revealDocumentStepErrors(outstandingDocumentStep);
            this.currentStep = outstandingDocumentStep;
            this.syncDocumentPaging();
            this.updateProgress();
            setTimeout(() => this.scrollToTop(), 50);
            return;
        }

        const formData = new FormData();
        formData.append('data', JSON.stringify({
            patientId: this.inviteDetails?.patientId ?? null,
            consentOnly: false,
            documentOnly: true,
            consents: [],
            forms: [],
        }));
        formData.append('shortToken', this.inviteShortToken);

        this.performSubmission(this.inviteRowId, formData);
    }

    submit() {
        if (this.isSubmitting) return;

        this.submissionRetryCount = 0;

        // §21.2. Nothing below this line applies: there is no signature pad, no consent array and
        // no personal detail to validate on a document-only invite.
        if (this.documentOnly) {
            this.submitDocumentOnly();
            return;
        }

        if (!this.consentOnly) {
            if (!this.personalInformationForm.valid) {
                this.showToastMessage("Please complete your personal information.");
                return;
            }

            if (!this.contactInformationForm.valid) {
                this.showToastMessage("Please complete your contact information.");
                return;
            }

            if (!this.isMedicalDetailsValid()) {
                this.showToastMessage(this.getMedicalDetailsValidationMessage());
                return;
            }

            if (!this.isEmergencyContactValid()) {
                this.showToastMessage(this.getEmergencyContactValidationMessage());
                return;
            }
        }

        if (!this.signatureForm.valid) {
            this.showToastMessage("Please provide your signature and acceptance of the provided information.");
            return;
        } else {
            this.signatureForm.patchValue({
                signature: this.signaturePadComponent?.getPadData()
            });
        }

        if (!this.isSignaturePadValid()) {
            this.showToastMessage("Please provide your signature and complete the signatory information.");
            return;
        }

        const consentFormsValues = this.consentForms.value;

        const requiresProcedureSide = consentFormsValues.some((form: any) => form.confirmSide && !form.selectedProcedureSide);
        const requiresImplant = consentFormsValues.some((form: any) => form.confirmImplant && !form.selectedImplant);
        const requiresTransfusion = consentFormsValues.some((form: any) => form.confirmTransfusion && !form.selectedTransfusion);
        const requiresRequiredConsent = consentFormsValues.some((form: any) => form.additionalRequiredConsent?.length && !form.selectedRequiredConsent);

        if (requiresProcedureSide) {
            this.showToastMessage("Please ensure you select a \"Procedure Side\" when completing your consent to continue");
            return;
        }
        if (requiresImplant) {
            this.showToastMessage("Please ensure you select an \"Implant\" when completing your consent to continue");
            return;
        }
        if (requiresTransfusion) {
            this.showToastMessage("Please ensure you select a \"Blood Transfusion\" option when completing your consent to continue");
            return;
        }
        if (requiresRequiredConsent) {
            this.showToastMessage("Please ensure you select a valid \"Required Consent\" option when completing your consent to continue");
            return;
        }

        // Practice forms. Every section of every form is checked — not `renderer.valid`, which
        // reports only the section that instance is currently paging to (§14.2). A form that fails
        // here TAKES THE PATIENT TO the step that needs attention with its errors already
        // revealed, which is exactly why `markAllTouched()` is called and `resetTouched()` is not
        // (§14.7): navigating through `goToStep` would undo the reveal one line after it happened
        // and land them on a step that looks fine with nothing on screen saying what is wrong.
        const firstInvalidFormStep = this.firstInvalidFormStepIndex();
        if (firstInvalidFormStep !== null) {
            this.revealFormStepErrors(firstInvalidFormStep);
            this.currentStep = firstInvalidFormStep;
            this.syncFormPaging();
            this.updateProgress();
            setTimeout(() => this.scrollToTop(), 50);
            return;
        }

        // Practice documents, swept the same way — a required one that has not been returned takes
        // the patient BACK TO ITS STEP rather than refusing from under the submit button, because
        // the thing they have to do is on that step and nowhere else. Note that nothing about the
        // document joins the payload below: it was returned out of band when they uploaded it.
        const outstandingDocumentStep = this.firstOutstandingDocumentStepIndex();
        if (outstandingDocumentStep !== null) {
            this.revealDocumentStepErrors(outstandingDocumentStep);
            this.currentStep = outstandingDocumentStep;
            this.syncDocumentPaging();
            this.updateProgress();
            setTimeout(() => this.scrollToTop(), 50);
            return;
        }

        setTimeout(() => {
            const details = this.getAllDetails();
            const guardianName = this.signaturePadComponent?.guardianName || '';
            const signatoryDelegation = this.signaturePadComponent?.signatoryDelegation || '';

            // One request id, one answers map, per form. The server validates every one of them
            // against the version it pinned when it raised the ask, prunes to the visible answers
            // and scores them; the client's own validation above is a courtesy that saves a round
            // trip, not an authority.
            const forms = this.formRequests.map((request) => ({
                id: request.id,
                answers: request.answers,
            }));

            let allDetails;
            if (this.consentOnly) {
                allDetails = {
                    ...details,
                    guardianName,
                    signatoryDelegation,
                    consents: consentFormsValues,
                    forms,
                };
            } else {
                const dob: moment.Moment = this.personalInformationForm.value.dob;
                allDetails = {
                    ...details,
                    guardianName,
                    signatoryDelegation,
                    consents: consentFormsValues,
                    forms,
                    dob: dob.utc().startOf('day').toISOString()
                };
            }

            if (consentFormsValues.some((consent: any) => !consent.consentGiven)) {
                this.showToastMessage("Please accept all consents to proceed.");
                return;
            }

            if (!this.signatureForm.valid) {
                this.showToastMessage("Please provide your signature and acceptance of the provided information.");
                return;
            }

            const formData = new FormData();
            formData.append('data', JSON.stringify(allDetails));
            const signatureDataUrl = this.signatureForm.get('signature')?.value;
            if (signatureDataUrl) {
                const signatureBlob = this.dataURLToBlob(signatureDataUrl);
                formData.append('signature', signatureBlob, 'signature.png');
            }
            const inviteToken = this.inviteToken ?? this.inviteDetails?.shortToken ?? '';
            formData.append('shortToken', inviteToken);

            this.performSubmission(details.id, formData);
        }, 200)
    }

    private setMainSubmissionOverlayCopy(): void {
        this.submissionOverlayMessage = this.documentOnly
            ? 'Finishing up…'
            : (this.consentOnly ? 'Submitting your consent…' : 'Submitting your information…');
        this.submissionOverlaySubtext = this.submissionRetryCount > 0
            ? 'Retrying. Please keep this page open.'
            : 'Please keep this page open. This may take a moment.';
    }

    private performSubmission(inviteId: string, formData: FormData): void {
        this.isSubmitting = true;
        this.setMainSubmissionOverlayCopy();

        const sub = this.patientInviteService.updateInvite(inviteId, formData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    // The server replays a success when this invite was already completed - a retry
                    // after a dropped response, rather than a fresh submission.
                    this.alreadySubmitted = result?.alreadyCompleted === true;
                    // Three modes, three sentences. A document-only invite never collected any
                    // "details", so saying they were updated would be a lie about what happened.
                    const message = this.documentOnly
                        ? (this.alreadySubmitted
                            ? 'This had already been completed.'
                            : 'Thank you — there is nothing further you need to do.')
                        : (this.alreadySubmitted
                            ? (this.consentOnly ? 'Your consent had already been received.' : 'Your details had already been received.')
                            : (this.consentOnly ? 'Your consent has been captured successfully.' : 'Your details have been updated successfully.'));
                    this.showToastMessage(message);
                    this.inviteName = this.getAllDetails().name;
                    this.mainSubmissionComplete = true;
                    this.showCompleted = true;
                    this.submissionError = false;
                    this.submissionErrorMessage = '';
                    this.isSubmitting = false;
                    this.submissionRetryCount = 0; // Reset retry count on success
                    
                    // Clear stored data on successful submission
                    this.clearStoredData();
                    
                    // Scroll to top for completion view
                    setTimeout(() => {
                        this.scrollToTop();
                    }, 100);
                    sub.unsubscribe();
                },
                error: (error) => {
                    console.error('Submission error:', error);
                    
                    // Check if we should retry
                    if (this.submissionRetryCount < this.MAX_RETRY_ATTEMPTS) {
                        this.submissionRetryCount++;
                        console.log(`Retrying submission (attempt ${this.submissionRetryCount}/${this.MAX_RETRY_ATTEMPTS})`);
                        this.showToastMessage('Submission failed, retrying...');
                        
                        // Retry after a short delay
                        setTimeout(() => {
                            this.performSubmission(inviteId, formData);
                        }, 1000);
                    } else {
                        this.isSubmitting = false;
                        this.submissionError = true;
                        // The documents themselves are ALREADY WITH THE PRACTICE — they were sent
                        // on their own step (§20.2). Only the "I am finished" is what failed, so
                        // the wording must not make a patient think their pages were lost.
                        this.submissionErrorMessage = this.documentOnly ?
                            'Your documents were sent, but we could not finish off. Please check your connection and try again.' :
                            (this.consentOnly ?
                                'Failed to save your consent response after retry. Please check your connection and try again.' :
                                'Failed to update your details after retry. Please check your connection and try again.');
                        this.showCompleted = true; // Show the completion step to display error state
                        
                        // Scroll to top for error view
                        setTimeout(() => {
                            this.scrollToTop();
                        }, 100);
                    }
                    sub.unsubscribe();
                }
            });
    }

    fullScreenConsentForm: any = null;
    @ViewChild('fullScreenDialog') fullScreenDialog?: TemplateRef<any>;

    viewConsentFullscreen(consentForm: FormGroup) {
        this.fullScreenConsentForm = consentForm.value;
        this.dialog.open(this.fullScreenDialog!);
    }

    downloadConsentPDF(consentForm: FormGroup) {
        const sub = this.patientInviteService.downloadConsentPDF(consentForm.value)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                async (blob: Blob) => {
                    await this.fileDownloadService.download(blob, `${consentForm.value.consentLabel}_${moment().valueOf()}.pdf`);
                    sub.unsubscribe();
                },
                (error) => {
                    console.error('Error downloading consent PDF:', error);
                    this.showToastMessage('There was an downloading the consent document. Please try again.');
                    sub.unsubscribe();
                });
    }

    retrySubmission() {
        this.submissionError = false;
        this.submissionErrorMessage = '';
        this.isSubmitting = false;
        this.submissionRetryCount = 0; // Reset retry count for new attempt
        this.showCompleted = false; // Hide completion step to go back to signature step
        
        // Reset signature form and pad
        this.signatureForm.patchValue({ signature: '' });
        if (this.signaturePadComponent) {
            this.signaturePadComponent.clearPad();
        }
        
        // Go back to signature step
        this.currentStep = this.totalSteps - 1; // Signature step is second to last
        this.updateProgress();
        
        // Use setTimeout to ensure the template renders before scrolling
        setTimeout(() => {
            this.scrollToTop();
        }, 100);
    }

    closeWindow() {
        window.close();
    }

    reloadPage() {
        window.location.reload();
    }

    selectLiableIdTypeOption(selection: string) {
        this.liableIdTypeSelection = selection;
        if (selection === 'sa-id') {
            this.personLiableForm.patchValue({
                liableIdType: 'id',
                liablePassportNumber: '',
                liablePassportCountry: ''
            });
            // Validators
            this.personLiableForm.get('liableIdNumber')?.setValidators([SaIdValidator.isValidSaId()]);
            this.personLiableForm.get('liablePassportNumber')?.clearValidators();
            this.personLiableForm.get('liablePassportCountry')?.clearValidators();
        } else if (selection === 'foreign-passport') {
            this.personLiableForm.patchValue({
                liableIdType: 'passport',
                liableIdNumber: '',
                liablePassportCountry: ''
            });
            this.personLiableForm.get('liableIdNumber')?.clearValidators();
            this.personLiableForm.get('liablePassportNumber')?.setValidators([Validators.required]);
            this.personLiableForm.get('liablePassportCountry')?.setValidators([Validators.required]);
        }
        this.personLiableForm.get('liableIdNumber')?.updateValueAndValidity();
        this.personLiableForm.get('liablePassportNumber')?.updateValueAndValidity();
        this.personLiableForm.get('liablePassportCountry')?.updateValueAndValidity();
        FormUtils.showAllFormErrorStates(this.personLiableForm);
    }

    selectEmergencyContactOption(hasEmergencyContact: boolean) {
        this.emergencyContactForm.patchValue({ hasEmergencyContact });
        
        if (hasEmergencyContact) {
            // Don't add required validators to individual fields - let the custom validator handle it
            // This allows users to fill in either primary or secondary contact
        } else {
            // Clear the form values when user chooses not to provide emergency contacts
            this.emergencyContactForm.patchValue({
                emergencyContact: '',
                emergencyContactRelationship: '',
                emergencyContactMobile: '',
                emergencyContactPhone: '',
                emergencyContactWork: '',
                emergencyContactEmail: '',
                emergencyContactAddress: '',
                emergencyContactAddressCode: '',
                emergencyContact2: '',
                emergencyContactRelationship2: '',
                emergencyContactMobile2: '',
                emergencyContactPhone2: '',
                emergencyContactWork2: '',
                emergencyContactEmail2: '',
                emergencyContactAddress2: '',
                emergencyContactAddressCode2: ''
            });
        }
        
        // Update form validity
        this.emergencyContactForm.updateValueAndValidity();
        
        this.updateStepCompletionStatus();
    }

    skipReferralSource() {
        // User chose to skip, just close the window
        this.closeWindow();
    }

    /* ── The patient app (tasks/patient-app.md §15): a second welcome card, "Open the app" or "Continue here" ── */

    appHandoffBusy = false;
    appHandoffError = '';

    /** Whether to offer it: the server decides from the group's opt-in, the app's configuration and the invite being open. */
    get patientAppOffered(): boolean {
        return this.inviteDetails?.patientAppOffered === true;
    }

    get patientAppName(): string {
        return this.inviteDetails?.patientAppName || 'MedicAI';
    }

    /** The second welcome card, about the app, shown after "Get Started" when the app is offered. */
    showsAppCard = false;

    /**
     * "Get Started": the app card first when the app is offered, otherwise the first step. A document-only invite with
     * nothing left to return finishes on the welcome screen (`showsFinishButton`) and never gets here.
     */
    getStarted() {
        if (this.patientAppOffered && !this.showsAppCard) {
            this.showsAppCard = true;
            window.scrollTo?.({ top: 0 });
            return;
        }
        this.nextStep();
    }

    /**
     * "Open the app": the whole invite is handed to the app, where the patient creates an account or signs in
     * and completes it. The invite token travels in the request body. Any refusal leaves them here, where "Continue
     * here" still works exactly as before.
     */
    useThePatientApp() {
        const token = this.inviteShortToken;
        if (!token || this.appHandoffBusy) return;
        this.appHandoffBusy = true;
        this.appHandoffError = '';
        this.patientInviteService.requestAppInviteHandoff(token)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => window.location.assign(result.url),
                error: () => {
                    this.appHandoffBusy = false;
                    this.appHandoffError = `${this.patientAppName} is not available for this invite just now. Please continue here instead.`;
                },
            });
    }

    submitReferralSource() {
        if (this.isSubmitting) return;

        const referralData = this.referralSourceForm.value;
        const inviteToken = this.inviteToken ?? this.inviteDetails?.shortToken;
        
        if (!inviteToken) {
            this.showToastMessage('Unable to save referral information. You can still close this window.');
            setTimeout(() => {
                this.closeWindow();
            }, 2000);
            return;
        }

        this.isSubmitting = true;
        this.submissionOverlayMessage = 'Saving your response…';
        this.submissionOverlaySubtext = 'Please keep this page open.';

        const sub = this.patientInviteService.updatePatientReferralSourceByInviteToken(inviteToken, referralData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.showToastMessage('Thank you for your feedback!');
                    this.isSubmitting = false;
                    // Close the window after successful submission
                    setTimeout(() => {
                        this.closeWindow();
                    }, 1000);
                    sub.unsubscribe();
                },
                error: (error) => {
                    this.isSubmitting = false;
                    this.showToastMessage('Failed to save your response. You can still close this window.');
                    console.error(error);
                    setTimeout(() => {
                        this.closeWindow();
                    }, 2000);
                    sub.unsubscribe();
                }
            });
    }

    // Local Storage Persistence Methods
    private getStorageKey(): string {
        return `${this.STORAGE_KEY_PREFIX}${this.inviteToken}`;
    }

    private saveFormDataToStorage(): void {
        if (!this.inviteToken) return;

        try {
            const formData = {
                token: this.inviteToken,
                timestamp: new Date().toISOString(),
                currentStep: this.currentStep,
                personalInformation: this.personalInformationForm.value,
                contactInformation: this.contactInformationForm.value,
                emergencyContact: this.emergencyContactForm.value,
                medicalInsurance: this.medicalInsuranceForm.value,
                personLiable: this.personLiableForm.value,
                // Note: Signature form is not tracked in local storage
                allergy: this.allergyForm.value,
                medicalHistory: this.medicalHistoryForm.value,
                medicationHistory: this.medicationHistoryForm.value,
                medicalTeam: this.medicalTeamForm.value,
                referralSource: this.referralSourceForm.value,
                consentForms: this.consentForms.value,
                // Practice-form answers ride the SAME localStorage mechanism as everything else,
                // keyed by request id so a form the practice has since revoked simply finds no
                // home on restore. See the note on `formRequests`: there is deliberately no
                // server-side draft on this path (§14.4).
                formAnswers: this.formRequests.reduce((map: any, request) => {
                    map[request.id] = request.answers;
                    return map;
                }, {}),
                // Additional state variables
                accountHolderSame: this.accountHolderSame,
                idTypeSelection: this.idTypeSelection,
                liableIdTypeSelection: this.liableIdTypeSelection,
                consentOnly: this.consentOnly
            };

            localStorage.setItem(this.getStorageKey(), JSON.stringify(formData));
        } catch (error) {
            console.warn('Failed to save form data to localStorage:', error);
        }
    }

    private loadFormDataFromStorage(): any | null {
        if (!this.inviteToken) return null;

        try {
            const storedData = localStorage.getItem(this.getStorageKey());
            if (!storedData) return null;

            const formData = JSON.parse(storedData);
            
            // Check if the stored data is for the same token
            if (formData.token !== this.inviteToken) {
                this.clearStoredData();
                return null;
            }

            return formData;
        } catch (error) {
            console.warn('Failed to load form data from localStorage:', error);
            this.clearStoredData();
            return null;
        }
    }

    private clearStoredData(): void {
        if (!this.inviteToken) return;
        
        try {
            localStorage.removeItem(this.getStorageKey());
        } catch (error) {
            console.warn('Failed to clear stored data from localStorage:', error);
        }
    }

    private async promptUserForDataRestore(): Promise<boolean> {
        return this.feedbackService.showConfirmation(
            'Autosaved Data Detected',
            'We found previously saved data for this form. Would you like to continue where you left off?',
            undefined,
            'Start fresh',
            'Continue with saved data'
        );
    }

    private async checkForStoredData(): Promise<void> {
        const storedData = this.loadFormDataFromStorage();
        
        if (storedData) {
            const shouldRestore = await this.promptUserForDataRestore();
            
            if (shouldRestore) {
                this.restoreFormData(storedData);
                this.showToastMessage('Your previous data has been restored.');
            } else {
                this.clearStoredData();
            }
        }
    }

    private restoreFormData(formData: any): void {
        try {
            // Reset error states when restoring data
            this.submissionError = false;
            this.submissionErrorMessage = '';
            this.isSubmitting = false;
            this.submissionRetryCount = 0;
            this.showCompleted = false;
            
            // Restore form values
            this.personalInformationForm.patchValue(formData.personalInformation || {});
            this.contactInformationForm.patchValue(formData.contactInformation || {});
            this.emergencyContactForm.patchValue(formData.emergencyContact || {});
            this.medicalInsuranceForm.patchValue(formData.medicalInsurance || {});
            this.personLiableForm.patchValue(formData.personLiable || {});
            // Note: Signature form is not restored from local storage
            this.referralSourceForm.patchValue(formData.referralSource || {});

            // Restore nested components by setting their input properties
            if (formData.allergy) {
                this.allergyForm.patchValue(formData.allergy);
                // The nested components will automatically update via their @Input properties
                // when the form values are patched, so we don't need to call the change methods
            }

            if (formData.medicalHistory) {
                this.medicalHistoryForm.patchValue(formData.medicalHistory);
                // The nested components will automatically update via their @Input properties
            }

            if (formData.medicationHistory) {
                this.medicationHistoryForm.patchValue(formData.medicationHistory);
                // The nested components will automatically update via their @Input properties
            }

            if (formData.medicalTeam) {
                this.medicalTeamForm.patchValue(formData.medicalTeam);
                // The nested components will automatically update via their @Input properties
            }

            // Note: Signature pad data is not restored from local storage

            // Restore consent forms
            if (formData.consentForms && Array.isArray(formData.consentForms)) {
                formData.consentForms.forEach((consentData: any, index: number) => {
                    if (this.consentForms.at(index)) {
                        this.consentForms.at(index).patchValue(consentData);
                    }
                });
            }

            // Restore practice-form answers. Written into `answers`, which is bound to the
            // renderer's `[value]` — the renderer rebuilds its controls from it and re-derives
            // visibility and validity, so a half-finished form comes back exactly as it was left.
            // Values go in RAW, with no coercion (§14.2 decision 6): a draft holding '3' where a
            // scale wants 3 must reach the validator as '3', because that is what the server sees.
            if (formData.formAnswers && typeof formData.formAnswers === 'object') {
                for (const request of this.formRequests) {
                    const saved = formData.formAnswers[request.id];
                    if (saved && typeof saved === 'object') request.answers = saved;
                }
            }

            // Restore state variables
            this.accountHolderSame = formData.accountHolderSame;
            this.idTypeSelection = formData.idTypeSelection || '';
            this.liableIdTypeSelection = formData.liableIdTypeSelection || '';
            this.consentOnly = formData.consentOnly || false;

            // Restore current step
            this.currentStep = formData.currentStep || 0;
            this.updateProgress();

            // Update form states
            this.updateStepCompletionStatus();
            this.updateInviteName();
            this.calculateInitials();
            this.calculateLiableInitials();

            this.hasRestoredData = true;
        } catch (error) {
            console.warn('Failed to restore form data:', error);
            this.showToastMessage('Failed to restore saved data. Starting fresh.');
        }
    }

    private autoSaveFormData(): void {
        // Only auto-save if we have a token and haven't just restored data
        if (!this.inviteToken || this.hasRestoredData) {
            this.hasRestoredData = false; // Reset the flag after first auto-save
            return;
        }

        // Debounce auto-save to avoid excessive localStorage writes
        setTimeout(() => {
            this.saveFormDataToStorage();
        }, 1000);
    }

    // Public method to manually clear stored data (useful for testing or manual cleanup)
    public clearAllStoredData(): void {
        if (!this.inviteToken) return;
        this.clearStoredData();
        this.showToastMessage('Stored data has been cleared.');
    }

} 